// src/views/admin/LiveReadingsPage.js
// --- UPDATE (weather v2): RainNowPanel under the AI weather card (rain now, why, staff rain report).
// --- END UPDATE
// --- UPDATE (sensor tables v2): sensors no longer vanish from the Sensor Readings tables after a refresh.
//   • Every sensor lands in exactly one table (sensors/sensorKind.js — same rule as the backend). Types other
//     than exactly 'pressure' / 'water_level' used to fall into neither table.
//   • A failed or partial refresh keeps the last good list (it used to empty the tables after 3 retries,
//     e.g. when the browser's sessionStorage was full); cache writes can no longer break a load.
//   • The full reload no longer re-runs on every live-connection flip — once on open, then on Refresh and
//     after a real reconnect (≥ 30 s since the last load); an older load can't overwrite a newer one.
//   • Tab badges count the rows each table shows (sensors ∪ devices sending readings). The Pressure badge
//     used to count readings, not sensors.
// --- END UPDATE
// ============================================================================
// Merged: v7 Production Base + Water-Level Integration + Digital Twin Engine
// Updated: Fixed Missing Constants and Injected Missing Utilities/Components
// Updated: Bulletproof Global WebSocket Integration
// Updated: Caching & Performance Optimizations (Session Storage & useMemo)
//
// --- UPDATE: PIPELINE CLEARANCE DATA -----------------------------------------
// Wires this page to the pipeline clearance fields (depth_m, depth_source,
// status, pressure_zone). All new UI lives in two dedicated components so this
// file does not grow further:
//
//   components/pipelines/PipelineClearancePanel.js — register-wide coverage
//       and the inline depth backfill worklist
//   components/pipelines/PipelineEditDrawer.js     — edit one pipeline's
//       clearance fields
//
// Everything added here is inside // --- UPDATE markers. No existing logic was
// altered.
//
// --- UPDATE: AI WEATHER & ML INTELLIGENCE -------------------------------------
//   services/aiWeatherService.js          — Open-Meteo feed + edge flood model
//   hooks/useAiWeatherIntelligence.js     — merges server ML (WebSocket) with edge
//   components/ai/AiWeatherCommandCenter  — weather + fleet risk + top wells panel
// The ai_flood_prediction handler now keeps every field the server sends, accepts
// batches, and timestamps each prediction. Merged predictions + one shared weather
// feed now reach WellManagementPanel and WaterLevelTable. WebSocket status
// listeners are now removed correctly on cleanup (they previously leaked).
// --- END UPDATE
// ============================================================================

import React, { 
  useState, useEffect, useRef, useCallback, useMemo, useReducer, createContext, useContext, memo 
} from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Box, Heading, useColorModeValue, Tag, Spinner, Text, Flex,
  Alert, AlertIcon, AlertTitle, AlertDescription, Button, Icon,
  HStack, useDisclosure, useToast, Divider, VStack, Badge,
  Tooltip, IconButton, Menu, MenuButton, MenuList, MenuItem,
  Switch, FormControl, FormLabel, Select, Input, InputGroup,
  InputLeftElement, Stat, StatLabel, StatNumber, StatHelpText,
  StatArrow, Grid, useBreakpointValue,
  Drawer, DrawerBody, DrawerHeader, DrawerOverlay,
  DrawerContent, DrawerCloseButton, DrawerFooter,
  SimpleGrid, Tabs, TabList, TabPanels, Tab, TabPanel,
  Collapse, Progress
} from '@chakra-ui/react';
import { ChevronUpIcon, ChevronDownIcon } from '@chakra-ui/icons';
import {
  MdAdd, MdMyLocation, MdTimeline, MdEditOff, MdRefresh,
  MdSettings, MdBugReport, MdFilterList, MdSearch,
  MdNotifications, MdWarning, MdCheckCircle, MdError,
  MdLink, MdLinkOff, MdMoreVert, MdVisibility,
  MdVisibilityOff, MdDownload, MdUpload, MdSpeed,
  MdRouter, MdSensors, MdWaves, MdWaterDrop,
  // --- UPDATE: icons for the clearance tab and banner
  MdShield, MdErrorOutline,
  // --- END UPDATE
  // --- UPDATE: AI / weather KPI icons
  MdAutoAwesome, MdGrain,
  // --- END UPDATE
} from 'react-icons/md';
import { 
  FaInfoCircle, FaChartLine, FaExpand, FaDownload, FaCog,
  FaServer, FaBell, FaExclamationTriangle, FaCity, FaSignal,
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

// Components
import ReadingTable    from '../../components/ReadingTable';
import WaterLevelTable from '../../components/WaterLevelTable';
import GisMapView      from '../../components/public_utilities/GisMapView';
import CreatePipelineForm from '../../components/pipelines/CreatePipelineForm';
import PlaceSensorModal   from '../../components/sensors/PlaceSensorModal';
import PipelineInfoDrawer from '../../components/pipelines/PipelineInfoDrawer';
import BindAssetDrawer from '../../components/sensors/BindAssetDrawer';
import MapFilterBar from '../../components/public_utilities/MapFilterBar';
import WellManagementPanel from '../../components/wells/WellManagementPanel';
// --- AI FLOOD & ROAD INUNDATION ANALYTICS ---
import { useFloodRiskModel } from '../../hooks/useFloodRiskModel';
import FloodInundationAlert from '../../components/wells/FloodInundationAlert';

// --- UPDATE: new pipeline clearance components ------------------------------
import PipelineClearancePanel from '../../components/pipelines/PipelineClearancePanel';
import PipelineEditDrawer from '../../components/pipelines/PipelineEditDrawer';
import PipelineDirectory from '../../components/pipelines/PipelineDirectory';
// --- END UPDATE

// --- UPDATE: AI weather & ML intelligence ----------------------------------
import { useAiWeatherIntelligence } from '../../hooks/useAiWeatherIntelligence';
import AiWeatherCommandCenter from '../../components/ai/AiWeatherCommandCenter';
import RainNowPanel from '../../components/ai/RainNowPanel'; // --- UPDATE (weather v2)
// --- UPDATE (wells v1): Well & Drainage Intelligence — every well in the register, live over WebSocket
import { useWellIntel } from '../../hooks/useWellIntel';
import WellIntelCommandStrip from '../../components/wells/WellIntelCommandStrip';
import WellOpsTracker from '../../components/wells/WellOpsTracker';
import WellAiExplainer from '../../components/wells/WellAiExplainer';
import ElevationBackfillPanel from '../../components/elevation/ElevationBackfillPanel'; // --- UPDATE (wells v1.3)
import { buildWellWorkOrderDraft } from '../../services/wellIntelService';
import { isWellType } from '../../components/wells/wellIntelUi'; // --- UPDATE (wells v1.2)
// --- END UPDATE
// --- END UPDATE

// Services
import { getAuthToken, clearAuthData } from '../../utils/auth';
import webSocketService   from '../../services/websocketService';
import * as PipelineService from '../../services/pipelineService';
import * as SensorService   from '../../services/sensorService';
import * as AssetService    from '../../services/assetService';
import digitalTwinService, { SENSOR_REGISTRY } from '../../services/digitalTwinService';
import ViewSensorDetails from '../../components/sensors/ViewSensorDetails';
import ViewHistory from '../../components/sensors/ViewHistory';
import { splitSensorsByKind, isWaterLevelSensor, isPressureSensor, countTableRows } from '../../components/sensors/sensorKind'; // --- UPDATE (sensor tables v2)

// =========================================================================
// ENTERPRISE FIX: IMPORT THE GLOBAL REAL-TIME CONTEXT
// =========================================================================
import { useNotifications } from '../../contexts/NotificationContext';
import { config } from '../../config';

const API_BASE_URL = config.apiUrl.replace(/\/api\/v1$/, '');

// --- UPDATE (sensor tables v2): sessionStorage is ~5 MB. A full quota (large asset / pipeline registers)
// threw inside the loader, which retried 3 times and then emptied the sensor tables. Cache writes are now
// best-effort: a write that does not fit removes that key instead of breaking the load.
const cacheWrite = (key, value) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (_) {
    try { sessionStorage.removeItem(key); } catch (__) { /* storage unavailable */ }
  }
};
const cacheRead = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    const v = raw ? JSON.parse(raw) : null;
    return Array.isArray(v) ? v : null;
  } catch (_) {
    return null;
  }
};
// --- END UPDATE

const MotionBox  = motion(Box);
const MotionFlex = motion(Flex);

// ============================================================================
// SECTION 0: UTILITY CLASSES & VALIDATORS (Injected to fix no-undef)
// ============================================================================

class DebugLogger {
  constructor(context) {
    this.context = context;
    this.logs = [];
    this.subscribers = [];
  }
  subscribe(callback) {
    this.subscribers.push(callback);
    return () => { this.subscribers = this.subscribers.filter(sub => sub !== callback); };
  }
  _emit(level, message, data) {
    const logEntry = { level, message, data, timestamp: Date.now(), context: this.context };
    this.logs.push(logEntry);
    this.subscribers.forEach(sub => sub(logEntry));
  }
  info(message, data) { this._emit('info', message, data); }
  warn(message, data) { this._emit('warn', message, data); }
  error(message, data) { this._emit('error', message, data); }
  success(message, data) { this._emit('success', message, data); }
  debug(message, data) { this._emit('debug', message, data); }
  clear() {
    this.logs = [];
    this._emit('info', 'Logs cleared', null);
  }
}

class PerformanceMonitor {
  constructor() {
    this.timers = new Map();
  }
  startTimer(label) {
    this.timers.set(label, performance.now());
  }
  endTimer(label) {
    const start = this.timers.get(label);
    if (start) {
      const duration = performance.now() - start;
      this.timers.delete(label);
      return duration;
    }
    return 0;
  }
}

const DataValidator = {
  validateReading: (data) => {
    if (!data) return { isValid: false, errors: ['Null reading data'] };
    const id = data.device_id || data.deviceId;
    if (!id) return { isValid: false, errors: ['Missing device ID'] };
    return { isValid: true, errors: [] };
  },
  validatePipeline: (pipeline) => {
    if (!pipeline || !pipeline.id) return { isValid: false, errors: ['Invalid pipeline'] };
    return { isValid: true, errors: [] };
  },
  validateSensor: (sensor) => {
    if (!sensor || (!sensor.device_id && !sensor.id)) return { isValid: false, errors: ['Invalid sensor'] };
    return { isValid: true, errors: [] };
  },
  validateCoordinates: (coords) => {
    if (!coords || typeof coords.lat !== 'number' || typeof coords.lng !== 'number') {
      return { isValid: false, errors: ['Invalid coordinate format'] };
    }
    if (coords.lat < -90 || coords.lat > 90 || coords.lng < -180 || coords.lng > 180) {
      return { isValid: false, errors: ['Coordinates out of bounds'] };
    }
    return { isValid: true, errors: [] };
  }
};

const AnimatedConnectionStatus = memo(({ isConnected, isConnecting, authError }) => {
  let statusText = 'Disconnected';
  let color = 'red.500';
  let pulseColor = 'rgba(239, 68, 68, 0.4)';

  if (isConnecting) {
    statusText = 'Connecting...';
    color = 'yellow.500';
    pulseColor = 'rgba(234, 179, 8, 0.4)';
  } else if (isConnected) {
    statusText = 'Live';
    color = 'green.500';
    pulseColor = 'rgba(34, 197, 94, 0.4)';
  }

  if (authError) {
    statusText = 'Auth Error';
    color = 'red.600';
  }

  return (
    <Tooltip label={authError || 'WebSocket Data Stream'} hasArrow>
      <Flex align="center" gap={2} px={3} py={1} bg={useColorModeValue('white', 'gray.800')} borderRadius="full" borderWidth="1px" borderColor={useColorModeValue('gray.200', 'gray.700')} boxShadow="sm">
        <Box position="relative" w="3" h="3">
          {isConnected && (
            <Box as={motion.div} position="absolute" top="0" left="0" w="100%" h="100%" borderRadius="full" bg={pulseColor} animate={{ scale: [1, 2], opacity: [0.8, 0] }} transition={{ duration: 1.5, repeat: Infinity }} />
          )}
          {isConnecting && <Spinner size="xs" color={color} position="absolute" top="-1px" left="-1px" />}
          {!isConnecting && <Box w="3" h="3" bg={color} borderRadius="full" />}
        </Box>
        <Text fontSize="xs" fontWeight="700" color={color}>{statusText}</Text>
      </Flex>
    </Tooltip>
  );
});

// ============================================================================
// SECTION 1: DIGITAL TWIN ENGINE COMPONENTS (Inlined for Enterprise Build)
// ============================================================================

const TWIN_VERSION = '5.0.0-enterprise';
const CHART_POINT_COUNT  = 60;
const TOAST_DURATION_MS  = 4000;
const MAX_HEALTH_HISTORY = 200;
const WS_STATES = { CONNECTING: 'CONNECTING', LIVE: 'LIVE', DEGRADED: 'DEGRADED', OFFLINE: 'OFFLINE', PAUSED: 'PAUSED' };
const ALARM_PRIORITY = { critical: 0, warning: 1, anomaly: 2, info: 3 };
const KPI_COLORS = { availability: '#00d4aa', mttr: '#60a5fa', mtbf: '#a78bfa', oee: '#f59e0b', alarms: '#ef4444', sensors: '#94a3b8' };

const TABS = [
  { id: 'telemetry',    icon: '◈', label: 'TELEMETRY'   },
  { id: 'forecast',     icon: '◉', label: 'FORECAST'    },
  { id: 'alarms',       icon: '⚠', label: 'ALARMS'      },
  { id: 'kpis',         icon: '▦', label: 'KPIs'        },
  { id: 'network',      icon: '⬡', label: 'NETWORK'      },
  { id: 'maintenance',  icon: '⚙', label: 'MAINTENANCE' },
  { id: 'audit',        icon: '≡', label: 'AUDIT LOG'   },
];

const TwinContext = createContext(null);

const twinReducer = (state, action) => {
  switch (action.type) {
    case 'INGEST_READINGS': {
      const { batch, ts } = action.payload;
      const newReadings    = { ...state.readings };
      const newHistory     = { ...state.sensorHistory };
      batch.forEach((normData) => {
        const sensorId = normData.sensorId;
        newReadings[sensorId] = { value: normData.value, timestamp: normData.timestamp ?? ts, quality: normData.quality };
        const prev = newHistory[sensorId] ?? [];
        if (!isNaN(normData.value)) {
            newHistory[sensorId] = [...prev.slice(-(CHART_POINT_COUNT - 1)), normData.value];
        }
      });
      return { ...state, readings: newReadings, sensorHistory: newHistory, lastIngestionTs: ts, totalReadingsIngested: state.totalReadingsIngested + batch.length, snapshotDirty: true };
    }
    case 'SNAPSHOT_LOADING': return { ...state, isLoadingSnapshot: true };
    case 'SNAPSHOT_READY': {
      const { snapshot, ts } = action.payload;
      const healthEntry = { ts, health: snapshot.health.score };
      const newHH = [...state.healthHistory.slice(-(MAX_HEALTH_HISTORY - 1)), healthEntry];
      return { ...state, snapshot, snapshotAge: ts, snapshotDirty: false, isLoadingSnapshot: false, healthHistory: newHH, lastError: null };
    }
    case 'SNAPSHOT_ERROR': return { ...state, isLoadingSnapshot: false, lastError: action.payload };
    case 'ALARMS_SYNC': {
      const merged = action.payload.map(a => ({ ...a, state: state.resolvedIds.has(a.id) ? 'resolved' : state.acknowledgedIds.has(a.id) ? 'acknowledged' : a.state }));
      const newCritical = merged.filter(a => a.severity === 'critical' && a.state === 'open' && !state.alarms.find(prev => prev.id === a.id));
      const toasts = newCritical.length > 0 ? [...state.toasts, ...newCritical.map(a => ({ id: `toast_${a.id}_${Date.now()}`, type: 'critical', message: `CRITICAL: ${a.detail}`, ts: Date.now() }))] : state.toasts;
      return { ...state, alarms: merged, toasts };
    }
    case 'ALARM_ACK': {
      const acked = new Set(state.acknowledgedIds); acked.add(action.payload.id);
      return { ...state, acknowledgedIds: acked, alarms: state.alarms.map(a => a.id === action.payload.id ? { ...a, state: 'acknowledged' } : a), alarmHistory: [...state.alarmHistory, { alarmId: action.payload.id, action: 'ACK', ts: Date.now() }] };
    }
    case 'ALARM_RESOLVE': {
      const resolved = new Set(state.resolvedIds); resolved.add(action.payload.id);
      return { ...state, resolvedIds: resolved, alarms: state.alarms.filter(a => a.id !== action.payload.id), alarmHistory: [...state.alarmHistory, { alarmId: action.payload.id, action: 'RESOLVE', resolution: action.payload.resolution, ts: Date.now() }] };
    }
    case 'WS_STATUS': return { ...state, wsStatus: action.payload, reconnectCount: action.payload === WS_STATES.CONNECTING ? state.reconnectCount + 1 : state.reconnectCount, uptimeSinceConnect: action.payload === WS_STATES.LIVE && !state.uptimeSinceConnect ? Date.now() : state.uptimeSinceConnect };
    case 'PING_RECEIVED': return { ...state, lastPingMs: action.payload };
    case 'TOAST_ADD': return { ...state, toasts: [...state.toasts, { ...action.payload, ts: Date.now() }] };
    case 'TOAST_DISMISS': return { ...state, toasts: state.toasts.filter(t => t.id !== action.payload) };
    default: return state;
  }
};

const useTwinEngine = ({ asset, sensors, liveEnabled, wsUrl, onCritical, externalReadings }) => {
  const [state, dispatch] = useReducer(twinReducer, { wsStatus: WS_STATES.CONNECTING, lastPingMs: null, reconnectCount: 0, readings: {}, sensorHistory: {}, healthHistory: [], lastIngestionTs: null, snapshot: null, snapshotAge: 0, snapshotDirty: false, alarms: [], acknowledgedIds: new Set(), resolvedIds: new Set(), alarmHistory: [], toasts: [], isLoadingSnapshot: false, lastError: null, totalReadingsIngested: 0, uptimeSinceConnect: null });
  const snapshotTs = useRef(0);

  useEffect(() => {
    if (!liveEnabled || !sensors?.length) { dispatch({ type: 'WS_STATUS', payload: WS_STATES.PAUSED }); return; }
    dispatch({ type: 'WS_STATUS', payload: WS_STATES.LIVE });
    if (externalReadings && Object.keys(externalReadings).length > 0) {
        const ts = Date.now();
        const batch = sensors.map(s => ({ sensorId: s.device_id || s.id, type: s.type || 'water_pressure', data: externalReadings[s.device_id || s.id] || {} })).filter(b => Object.keys(b.data).length > 0);
        if (batch.length > 0) {
            const normalizedBatch = digitalTwinService.ingestBatch(batch);
            dispatch({ type: 'INGEST_READINGS', payload: { batch: normalizedBatch, ts } });
            dispatch({ type: 'PING_RECEIVED', payload: Math.floor(Math.random() * 20) + 10 });
        }
    }
  }, [externalReadings, liveEnabled, sensors]);

  useEffect(() => {
    if (!asset || !sensors?.length || !state.snapshotDirty) return;
    const now = Date.now();
    if (now - snapshotTs.current < 500) return; 
    snapshotTs.current = now;
    dispatch({ type: 'SNAPSHOT_LOADING' });
    try {
      const linked = sensors.filter(s => !s.linked_entity_id || String(s.linked_entity_id) === String(asset.id ?? asset.assetId));
      const snapshot = digitalTwinService.buildSnapshot(asset, linked, state.readings, { healthHistory: state.healthHistory, networkGraph: asset.networkGraph ?? [], assetMeta: asset.meta ?? {} });
      dispatch({ type: 'SNAPSHOT_READY', payload: { snapshot, ts: now } });
      dispatch({ type: 'ALARMS_SYNC', payload: snapshot.alarms ?? [] });
      const criticals = (snapshot.alarms ?? []).filter(a => a.severity === 'critical' && a.state === 'open');
      if (criticals.length > 0 && typeof onCritical === 'function') onCritical(criticals, snapshot);
    } catch (err) { dispatch({ type: 'SNAPSHOT_ERROR', payload: err.message }); }
  }, [state.snapshotDirty, state.readings, asset, sensors, onCritical, state.healthHistory]);

  const acknowledgeAlarm = useCallback((id, by = 'operator') => { const alarm = digitalTwinService.acknowledgeAlarm(id, by); if (alarm) { dispatch({ type: 'ALARM_ACK', payload: { id } }); dispatch({ type: 'TOAST_ADD', payload: { id: `ack_${id}`, type: 'info', message: `Alarm acknowledged` } }); } }, []);
  const resolveAlarm = useCallback((id, by = 'operator', resolution = '') => { const alarm = digitalTwinService.resolveAlarm(id, by, resolution); if (alarm) { dispatch({ type: 'ALARM_RESOLVE', payload: { id, resolution } }); dispatch({ type: 'TOAST_ADD', payload: { id: `res_${id}`, type: 'success', message: `Alarm resolved` } }); } }, []);
  const dismissToast = useCallback(id => { dispatch({ type: 'TOAST_DISMISS', payload: id }); }, []);

  return { state, dispatch, acknowledgeAlarm, resolveAlarm, dismissToast };
};

const useSnapshotStats = (snapshot, healthHistory) => {
  return useMemo(() => {
    if (!snapshot) return null;
    const kpis = snapshot.kpis ?? {};
    const pred = snapshot.prediction ?? {};
    const openAlarms    = (snapshot.alarms ?? []).filter(a => a.state !== 'resolved');
    const computedKpis = healthHistory.length > 5 ? digitalTwinService.computeKPIs(healthHistory) : kpis;
    return { openAlarms, kpis: computedKpis, prediction: pred, healthScore: snapshot.health.score, healthColor: snapshot.health.color, healthLabel: snapshot.health.label, totalSensors: snapshot.sensors?.length ?? 0, activeSensors: (snapshot.sensors ?? []).filter(s => s.value != null).length, networkImpact: snapshot.network ?? {}, maintenance: snapshot.maintenance ?? [] };
  }, [snapshot, healthHistory]);
};

// Twin UI Components
const Sparkline = memo(({ data = [], color = '#00d4aa', width = 80, height = 24, showArea = false }) => {
  if (data.length < 2) return <svg width={width} height={height}><line x1="0" y1={height / 2} x2={width} y2={height / 2} stroke="rgba(255,255,255,0.08)" strokeWidth="1" strokeDasharray="2,2" /></svg>;
  const min = Math.min(...data), max = Math.max(...data), range = (max - min) || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1) * width).toFixed(1)},${(height - ((v - min) / range) * (height - 4) - 2).toFixed(1)}`);
  const [lx, ly] = pts[pts.length - 1].split(',');
  const areaPath = showArea ? `${pts.join(' ')} ${width},${height} 0,${height}` : null;
  return (
    <svg width={width} height={height} style={{ overflow: 'visible' }}>
      {showArea && <defs><linearGradient id={`sg_${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity="0.25" /><stop offset="100%" stopColor={color} stopOpacity="0.01" /></linearGradient></defs>}
      {showArea && <polygon points={areaPath} fill={`url(#sg_${color.replace('#','')})`} />}
      <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" opacity="0.9" />
      <circle cx={lx} cy={ly} r="2.5" fill={color} style={{ filter: `drop-shadow(0 0 3px ${color})` }} />
    </svg>
  );
});

const HealthGauge = memo(({ score, color, label, size = 140 }) => {
  const r = size * 0.386, cx = size / 2, cy = size / 2, circ = 2 * Math.PI * r, dash = ((score ?? 0) / 100) * circ;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} style={{ filter: `drop-shadow(0 0 14px ${color}33)`, flexShrink: 0 }}>
      <defs>
        <radialGradient id="gaugeInner" cx="50%" cy="50%" r="50%"><stop offset="0%" stopColor={color} stopOpacity="0.08" /><stop offset="100%" stopColor={color} stopOpacity="0.01" /></radialGradient>
        <filter id="gaugeGlow"><feGaussianBlur stdDeviation="2" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
      </defs>
      <circle cx={cx} cy={cy} r={r + 5} fill="url(#gaugeInner)" />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="10" strokeDasharray={`${circ * 0.75} ${circ}`} strokeDashoffset={circ * 0.125} strokeLinecap="round" />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round" strokeDasharray={`${Math.min(dash, circ * 0.75)} ${circ}`} strokeDashoffset={circ * 0.125} filter="url(#gaugeGlow)" style={{ transition: 'stroke-dasharray 1.4s cubic-bezier(0.34,1.56,0.64,1)', filter: `drop-shadow(0 0 6px ${color})` }} />
      <text x={cx} y={cy - 8} textAnchor="middle" fill={color} fontSize={size * 0.165} fontWeight="700" fontFamily="'JetBrains Mono', monospace">{Math.round(score ?? 0)}</text>
      <text x={cx} y={cy + 8} textAnchor="middle" fill="rgba(255,255,255,0.35)" fontSize={size * 0.065} fontFamily="'JetBrains Mono', monospace" letterSpacing="2">HEALTH</text>
      <text x={cx} y={cy + 22} textAnchor="middle" fill={color} fontSize={size * 0.074} fontFamily="'Barlow Condensed', sans-serif" fontWeight="600" letterSpacing="1.5">{label?.toUpperCase()}</text>
    </svg>
  );
});

const SensorCard = memo(({ sensor, history, expanded, onToggle }) => {
  const def = SENSOR_REGISTRY[sensor.type] ?? {};
  const stateColor = sensor.threshold?.state === 'critical' ? '#ef4444' : sensor.threshold?.state === 'warning' ? '#f59e0b' : '#00d4aa';
  const rangePct = sensor.value != null && def.min != null && def.max != null ? Math.min(100, Math.max(0, ((sensor.value - def.min) / ((def.max - def.min) || 1)) * 100)) : 0;
  return (
    <div className="sensor-card" data-state={sensor.threshold?.state ?? 'ok'} data-anomaly={sensor.anomaly?.isAnomaly ? 'true' : undefined} onClick={onToggle} style={{ cursor: 'pointer' }}>
      <div className="sc-category">{(def.category ?? 'General').toUpperCase()}</div>
      <div className="sc-header">
        <span className="sc-label">{sensor.label ?? sensor.type}</span>
        <span className="sc-badge" data-state={sensor.threshold?.state ?? 'ok'}>{sensor.threshold?.state?.toUpperCase() ?? 'OK'}</span>
      </div>
      <div className="sc-value-row">
        {sensor.value != null ? <span className="sc-value" style={{ color: stateColor }}>{Number(sensor.value).toFixed(2)}</span> : <span className="sc-value sc-nodata">N/A</span>}
        <span className="sc-unit">{sensor.unit}</span>
        <div className="sc-sparkline"><Sparkline data={history ?? []} color={stateColor} showArea /></div>
      </div>
      <div className="sc-bar-wrap">
        <div className="sc-bar-track"><div className="sc-bar-fill" style={{ width: `${rangePct}%`, background: stateColor, boxShadow: `0 0 5px ${stateColor}66` }} /></div>
        <div className="sc-bar-labels"><span>{def.min}</span><span className="sc-bar-pct">{rangePct.toFixed(0)}%</span><span>{def.max}</span></div>
      </div>
      {sensor.anomaly?.isAnomaly && <div className="sc-anomaly"><span>⚠ ANOMALY</span><span>Z = {sensor.anomaly.zScore}</span></div>}
    </div>
  );
});

const AlarmRow = memo(({ alarm, onAck, onResolve }) => {
  const [showResolveInput, setShowResolveInput] = useState(false);
  const [resolution, setResolution] = useState('');
  return (
    <div className={`alarm-row alarm-${alarm.severity}`} data-state={alarm.state}>
      <div className="alarm-severity-bar" />
      <div className="alarm-indicator"><div className="alarm-dot" /><span className="alarm-sev-label">{alarm.severity?.toUpperCase()}</span></div>
      <div className="alarm-body">
        <span className="alarm-detail">{alarm.detail}</span>
        <div className="alarm-meta"><span className="alarm-state">{alarm.state?.toUpperCase()}</span><span className="alarm-sensor">{alarm.sensorId}</span></div>
      </div>
      <div className="alarm-controls">
        {alarm.state === 'open' && <button onClick={() => onAck(alarm.id)} className="btn-xs btn-ack" title="Acknowledge">ACK</button>}
        {alarm.state !== 'resolved' && !showResolveInput && <button onClick={() => setShowResolveInput(true)} className="btn-xs btn-res" title="Resolve">RES</button>}
        {showResolveInput && (
          <div className="resolve-inline" onClick={e => e.stopPropagation()}>
            <input className="resolve-input" placeholder="Resolution note..." value={resolution} onChange={e => setResolution(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { onResolve(alarm.id, 'operator', resolution); setShowResolveInput(false); } if (e.key === 'Escape') setShowResolveInput(false); }} autoFocus />
            <button className="btn-xs btn-res" onClick={() => { onResolve(alarm.id, 'operator', resolution); setShowResolveInput(false); }}>✓</button>
          </div>
        )}
      </div>
    </div>
  );
});

const ToastStack = memo(({ toasts, onDismiss }) => {
  useEffect(() => { toasts.forEach(t => { const timer = setTimeout(() => onDismiss(t.id), TOAST_DURATION_MS); return () => clearTimeout(timer); }); }, [toasts, onDismiss]);
  return <div className="toast-stack">{toasts.slice(-4).map(t => (<div key={t.id} className={`toast toast-${t.type}`} onClick={() => onDismiss(t.id)}><span className="toast-dot" /><span className="toast-msg">{t.message}</span><button className="toast-x">✕</button></div>))}</div>;
});

const TelemetryTab = memo(({ sensors, sensorHistory }) => {
  const [expanded, setExpanded] = useState(null);
  return (
    <div className="telemetry-tab">
      <div className="dt-sensor-grid">
        {sensors.map(s => <SensorCard key={s.sensorId} sensor={s} history={sensorHistory[s.sensorId]} expanded={expanded === s.sensorId} onToggle={() => setExpanded(p => p === s.sensorId ? null : s.sensorId)} />)}
      </div>
    </div>
  );
});

const AlarmConsole = memo(({ alarms, onAck, onResolve }) => {
  const filtered = alarms.filter(a => a.state !== 'resolved');
  return (
    <div className="alarm-console">
      <div className="ac-list">
        {filtered.length === 0 ? <div className="no-alarms"><span className="na-icon">✓</span><span>No active alarms</span></div> : filtered.map(a => <AlarmRow key={a.id} alarm={a} onAck={onAck} onResolve={onResolve} />)}
      </div>
    </div>
  );
});

const HeaderBar = memo(({ asset, wsStatus, liveEnabled, onToggleLive, reconnectCount, uptimeSince, lastPingMs }) => {
  return (
    <header className="dt-header">
      <div className="dt-header-left">
        <div className="dt-asset-icon">⬡</div>
        <div className="dt-asset-info">
          <h1 className="dt-asset-name">{asset?.name ?? 'Digital Twin'}</h1>
          <div className="dt-asset-meta"><span className="dt-asset-type">{asset?.type ?? 'Asset'}</span><span className="dt-asset-id">ID: {asset?.id ?? '—'}</span><span className="dt-version">v{TWIN_VERSION}</span></div>
        </div>
      </div>
      <div className="dt-header-right">
        {lastPingMs != null && <div className="dt-ping"><span>◎</span><span>{lastPingMs}ms</span></div>}
        <button className={`dt-live-toggle ${liveEnabled ? 'live-on' : 'live-off'}`} onClick={onToggleLive}><span className="live-dot" />{liveEnabled ? 'LIVE' : 'PAUSED'}</button>
        <div className={`dt-sync sync-${wsStatus.toLowerCase()}`}><span className="sync-dot" /><span className="sync-status-text">{wsStatus}</span></div>
      </div>
    </header>
  );
});

const HealthCommand = memo(({ health, kpis, openAlarms, activeSensors, totalSensors }) => {
  return (
    <section className="dt-health-section">
      <div className="dt-gauge-wrap"><HealthGauge score={health.score} color={health.color} label={health.label} /></div>
      <div className="dt-kpi-matrix">
        {[
          { label: 'AVAILABILITY', value: `${(kpis.availability ?? 0).toFixed(1)}%`, color: KPI_COLORS.availability },
          { label: 'OEE', value: `${((kpis.oee ?? 0) * 100).toFixed(1)}%`, color: KPI_COLORS.oee },
          { label: 'MTTR', value: `${(kpis.mttr_hours ?? 0).toFixed(1)}h`, color: KPI_COLORS.mttr },
          { label: 'OPEN ALARMS', value: openAlarms.length, color: openAlarms.length > 0 ? '#ef4444' : KPI_COLORS.availability },
          { label: 'SENSORS', value: `${activeSensors}/${totalSensors}`, color: KPI_COLORS.sensors },
        ].map(k => (
          <div key={k.label} className="dt-kpi-cell"><span className="dt-kpi-val" style={{ color: k.color }}>{k.value}</span><span className="dt-kpi-lbl">{k.label}</span></div>
        ))}
      </div>
    </section>
  );
});

const TabRouter = memo(({ activeTab, snapshot, state, onAck, onResolve }) => {
  if (!snapshot) return <div className="dt-no-snapshot">Awaiting first snapshot...</div>;
  return (
    <div className="dt-content">
      {activeTab === 'telemetry' && <TelemetryTab sensors={snapshot.sensors ?? []} sensorHistory={state.sensorHistory} />}
      {activeTab === 'alarms' && <AlarmConsole alarms={state.alarms} onAck={onAck} onResolve={onResolve} />}
    </div>
  );
});

const DigitalTwinOverlay = ({ selectedAsset, sensors = [], healthHistory: externalHealthHistory = [], readings: externalReadings, wsUrl, onCriticalAlarm }) => {
  const [liveEnabled, setLiveEnabled] = useState(true);
  const [activeTab, setActiveTab]     = useState('telemetry');
  
  const { state, acknowledgeAlarm, resolveAlarm, dismissToast } = useTwinEngine({ asset: selectedAsset, sensors, liveEnabled, wsUrl, onCritical: onCriticalAlarm, externalReadings });
  const healthHistory = useMemo(() => { const external = externalHealthHistory.map((h, i) => ({ ts: Date.now() - (externalHealthHistory.length - i) * 10000, health: typeof h === 'number' ? h : h.health })); return [...external, ...state.healthHistory].slice(-MAX_HEALTH_HISTORY); }, [externalHealthHistory, state.healthHistory]);
  const stats = useSnapshotStats(state.snapshot, healthHistory);

  if (!selectedAsset) return <div className="dt-root dt-empty"><div className="dte-inner"><div className="dte-hex">⬡</div><h2 className="dte-title">DIGITAL TWIN ENGINE</h2><p className="dte-sub">Select an asset to initialize</p></div></div>;

  return (
    <TwinContext.Provider value={{ state, stats, activeTab, setActiveTab }}>
      <style>{GLOBAL_CSS}</style>
      <div className="dt-root">
        <div className="dt-scanlines" aria-hidden="true" />
        <div className="dt-grid-bg" aria-hidden="true" />
        <HeaderBar asset={selectedAsset} wsStatus={state.wsStatus} liveEnabled={liveEnabled} onToggleLive={() => setLiveEnabled(v => !v)} reconnectCount={state.reconnectCount} uptimeSince={state.uptimeSinceConnect} lastPingMs={state.lastPingMs} />
        {stats && <HealthCommand health={state.snapshot?.health ?? { score: 0, color: '#ef4444', label: 'Unknown' }} kpis={stats.kpis} openAlarms={stats.openAlarms} activeSensors={stats.activeSensors} totalSensors={stats.totalSensors} />}
        <nav className="dt-tabs" role="tablist">
          {TABS.slice(0,3).map(t => (
            <button key={t.id} role="tab" aria-selected={activeTab === t.id} className={`dt-tab ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>
              <span className="dt-tab-icon">{t.icon}</span><span className="dt-tab-label">{t.label}</span>
            </button>
          ))}
        </nav>
        <TabRouter activeTab={activeTab} snapshot={state.snapshot} state={state} stats={stats} onAck={acknowledgeAlarm} onResolve={resolveAlarm} assetId={selectedAsset?.id ?? ''} healthHistory={healthHistory} />
        <footer className="dt-footer">
          <div className="dt-footer-status">
            <span className="dt-readings-count">{state.totalReadingsIngested.toLocaleString()} readings</span>
            <span className="dt-gen-time">{state.snapshot ? `SNAP: ${new Date(state.snapshotAge).toLocaleTimeString()}` : '—'}</span>
          </div>
        </footer>
        <ToastStack toasts={state.toasts} onDismiss={dismissToast} />
      </div>
    </TwinContext.Provider>
  );
};

const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap');
:root { --bg: #070b12; --bg2: #0c1220; --bg3: #111827; --border: rgba(255,255,255,0.07); --cyan: #00d4aa; --amber: #f59e0b; --blue: #60a5fa; --red: #ef4444; --text: #e2e8f0; --text-muted: #64748b; --text-dim: #334155; --mono: 'JetBrains Mono', monospace; --display: 'Barlow Condensed', sans-serif; --radius: 6px; }
.dt-root { font-family: var(--mono); background: var(--bg); color: var(--text); width: 100%; min-height: 100vh; max-width: 680px; margin: 0 auto; position: relative; overflow: hidden; border: 1px solid var(--border); display: flex; flex-direction: column; }
.dt-scanlines { position: absolute; inset: 0; pointer-events: none; z-index: 10; background: repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px); }
.dt-grid-bg { position: absolute; inset: 0; pointer-events: none; background-image: linear-gradient(rgba(0,212,170,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,170,0.02) 1px, transparent 1px); background-size: 40px 40px; animation: gridShift 20s linear infinite; }
@keyframes gridShift { from { background-position: 0 0; } to { background-position: 40px 40px; } }
.dt-header { display: flex; align-items: center; justify-content: space-between; padding: 14px 20px; background: linear-gradient(180deg, rgba(0,212,170,0.05) 0%, transparent 100%); border-bottom: 1px solid var(--border); position: relative; z-index: 1; }
.dt-header::before { content: ''; position: absolute; left: 0; top: 0; width: 3px; height: 100%; background: var(--cyan); box-shadow: 0 0 12px var(--cyan); }
.dt-header-left { display: flex; align-items: center; gap: 12px; }
.dt-asset-icon { font-size: 28px; color: var(--cyan); filter: drop-shadow(0 0 8px var(--cyan)); animation: hexPulse 3s ease-in-out infinite; }
@keyframes hexPulse { 0%,100% { filter: drop-shadow(0 0 8px var(--cyan)); } 50% { filter: drop-shadow(0 0 16px var(--cyan)); } }
.dt-asset-info { display: flex; flex-direction: column; }
.dt-asset-name { font-family: var(--display); font-size: 20px; font-weight: 700; letter-spacing: 1px; color: var(--text); text-transform: uppercase; margin: 0; }
.dt-asset-meta { display: flex; gap: 8px; font-size: 10px; color: var(--text-muted); }
.dt-version { color: var(--cyan); opacity: 0.7; }
.dt-header-right { display: flex; align-items: center; gap: 10px; font-size: 10px; }
.dt-live-toggle { display: flex; align-items: center; gap: 6px; font-family: var(--mono); font-size: 10px; font-weight: 700; padding: 5px 10px; border-radius: 3px; cursor: pointer; border: 1px solid; letter-spacing: 1px; transition: all 0.2s; background: transparent; }
.dt-live-toggle.live-on  { color: var(--cyan); border-color: var(--cyan); }
.dt-live-toggle.live-off { color: var(--text-muted); border-color: var(--text-muted); }
.live-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--cyan); animation: liveBlink 1s ease-in-out infinite; }
.live-off .live-dot { background: var(--text-muted); animation: none; }
@keyframes liveBlink { 0%,100% { opacity: 1; } 50% { opacity: 0.2; } }
.dt-sync { display: flex; align-items: center; gap: 6px; font-size: 10px; letter-spacing: 1px; padding: 4px 8px; border-radius: 3px; border: 1px solid transparent; }
.sync-live { color: var(--cyan); border-color: rgba(0,212,170,0.3); background: rgba(0,212,170,0.07); }
.sync-desynced { color: var(--amber); border-color: rgba(245,158,11,0.3); background: rgba(245,158,11,0.07); }
.sync-dot { width: 5px; height: 5px; border-radius: 50%; background: currentColor; }
.dt-health-section { display: flex; align-items: center; gap: 0; padding: 16px 20px; background: var(--bg2); border-bottom: 1px solid var(--border); position: relative; z-index: 1; }
.dt-gauge-wrap { flex-shrink: 0; }
.dt-kpi-matrix { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0; flex: 1; padding-left: 20px; }
.dt-kpi-cell { display: flex; flex-direction: column; align-items: center; padding: 8px 4px; border-right: 1px solid var(--border); border-bottom: 1px solid var(--border); position: relative; }
.dt-kpi-cell:nth-child(n+4) { border-bottom: none; }
.dt-kpi-cell:nth-child(3n) { border-right: none; }
.dt-kpi-val { font-size: 15px; font-weight: 700; }
.dt-kpi-lbl { font-size: 8px; color: var(--text-muted); letter-spacing: 1px; margin-top: 2px; }
.dt-kpi-badge { position: absolute; top: 4px; right: 4px; background: rgba(239,68,68,0.2); color: var(--red); font-size: 7px; padding: 2px 4px; border-radius: 2px;}
.dt-tabs { display: flex; border-bottom: 1px solid var(--border); background: var(--bg2); overflow-x: auto; scrollbar-width: none; position: relative; z-index: 1; }
.dt-tab { font-family: var(--mono); font-size: 10px; font-weight: 600; letter-spacing: 1.5px; padding: 10px 16px; color: var(--text-muted); background: transparent; border: none; border-bottom: 2px solid transparent; cursor: pointer; white-space: nowrap; transition: all 0.2s; display: flex; align-items: center; gap: 6px; }
.dt-tab:hover { color: var(--text); }
.dt-tab.active { color: var(--cyan); border-bottom-color: var(--cyan); background: rgba(0,212,170,0.05); }
.dt-tab-badge { background: var(--red); color: white; padding: 2px 5px; border-radius: 10px; font-size: 8px; }
.dt-content { flex: 1; overflow-y: auto; background: var(--bg); position: relative; z-index: 1; padding: 15px; }
.dt-sensor-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.sensor-card { background: var(--bg2); padding: 12px; border-radius: 6px; border: 1px solid var(--border); transition: all 0.2s; }
.sensor-card[data-state="critical"] { border-color: rgba(239,68,68,0.5); background: rgba(239,68,68,0.05); }
.sensor-card:hover { border-color: var(--cyan); }
.sc-category { font-size: 8px; color: var(--cyan); letter-spacing: 1px; margin-bottom: 5px; }
.sc-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
.sc-label { font-size: 10px; color: var(--text); text-transform: uppercase; }
.sc-badge { font-size: 8px; padding: 2px 4px; border-radius: 2px; }
.sc-badge[data-state="ok"] { background: rgba(0,212,170,0.1); color: var(--cyan); }
.sc-badge[data-state="warning"] { background: rgba(245,158,11,0.1); color: var(--amber); }
.sc-badge[data-state="critical"] { background: rgba(239,68,68,0.1); color: var(--red); }
.sc-value-row { display: flex; align-items: baseline; gap: 4px; margin-bottom: 8px; }
.sc-value { font-size: 22px; font-weight: bold; }
.sc-nodata { color: var(--text-muted); }
.sc-unit { font-size: 10px; color: var(--text-muted); }
.sc-sparkline { margin-left: auto; }
.sc-bar-wrap { margin-top: 10px; }
.sc-bar-track { height: 4px; background: rgba(255,255,255,0.05); border-radius: 2px; overflow: hidden; }
.sc-bar-fill { height: 100%; transition: width 0.3s ease; }
.sc-bar-labels { display: flex; justify-content: space-between; font-size: 8px; color: var(--text-muted); margin-top: 4px; }
.sc-anomaly { margin-top: 10px; padding: 5px; background: rgba(239,68,68,0.1); color: var(--red); font-size: 9px; display: flex; justify-content: space-between; border-radius: 3px; }
.alarm-row { display: flex; align-items: center; gap: 12px; padding: 12px; background: var(--bg2); border: 1px solid var(--border); border-radius: 4px; margin-bottom: 8px; }
.alarm-critical { border-left: 3px solid var(--red); }
.alarm-warning { border-left: 3px solid var(--amber); }
.alarm-sev-label { font-size: 10px; font-weight: bold; }
.alarm-critical .alarm-sev-label { color: var(--red); }
.alarm-warning .alarm-sev-label { color: var(--amber); }
.alarm-body { flex: 1; }
.alarm-detail { display: block; font-size: 12px; color: var(--text); }
.alarm-meta { display: flex; gap: 10px; font-size: 9px; color: var(--text-muted); margin-top: 4px; }
.btn-xs { background: none; border: 1px solid var(--cyan); color: var(--cyan); padding: 4px 8px; font-size: 9px; cursor: pointer; border-radius: 3px; }
.btn-ack { border-color: var(--amber); color: var(--amber); }
.dt-footer { display: flex; align-items: center; gap: 10px; padding: 12px 20px; background: var(--bg2); border-top: 1px solid var(--border); z-index: 1; }
.btn-sim, .btn-export { background: transparent; border: 1px solid var(--cyan); color: var(--cyan); padding: 6px 12px; font-size: 10px; font-family: var(--mono); cursor: pointer; display: flex; align-items: center; gap: 5px; border-radius: 4px; }
.btn-export { border-color: var(--blue); color: var(--blue); }
.dt-footer-status { margin-left: auto; display: flex; gap: 15px; font-size: 10px; color: var(--text-muted); }
.dt-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,0.8); z-index: 1000; display: flex; align-items: center; justify-content: center; backdrop-filter: blur(4px); }
.sim-workbench { background: var(--bg); border: 1px solid var(--cyan); width: 800px; max-width: 90vw; border-radius: 8px; display: flex; flex-direction: column; }
.sw-header { padding: 15px; border-bottom: 1px solid var(--border); display: flex; justify-content: space-between; }
.sw-title { color: var(--cyan); font-weight: bold; font-size: 14px; }
.sw-body { display: flex; height: 500px; }
.sw-left { width: 50%; border-right: 1px solid var(--border); padding: 15px; overflow-y: auto; }
.sw-right { width: 50%; padding: 15px; background: var(--bg2); }
.sw-input-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.sw-input { background: var(--bg2); border: 1px solid var(--border); color: white; padding: 5px; width: 80px; font-family: var(--mono); }
.sw-run-btn { width: 100%; background: rgba(0,212,170,0.1); border: 1px solid var(--cyan); color: var(--cyan); padding: 10px; margin-top: 20px; cursor: pointer; }
.swr-health-compare { display: flex; justify-content: space-between; align-items: center; font-size: 24px; padding: 20px; background: var(--bg); border-radius: 6px; }
.dt-empty { display: flex; justify-content: center; align-items: center; min-height: 400px; color: var(--text-muted); flex-direction: column; }
.dte-hex { font-size: 40px; color: var(--border); margin-bottom: 10px; }
`;

// ============================================================================
// SECTION 4: DATA OVERLAY COMPONENT (Enterprise System Overview)
// ============================================================================

const getAlertSeverity = (count) => { if (count > 10) return 'high'; if (count > 0) return 'medium'; return 'low'; };
const getBgColor = (severity, colorMode) => { const colors = { high: colorMode === 'light' ? 'rgba(200, 30, 30, 0.95)' : 'rgba(120, 0, 0, 0.95)', medium: colorMode === 'light' ? 'rgba(200, 100, 30, 0.95)' : 'rgba(120, 60, 0, 0.95)', low: colorMode === 'light' ? 'rgba(70, 85, 100, 0.95)' : 'rgba(45, 55, 72, 0.95)' }; return colors[severity]; };

const DataOverlay = ({ assets = [], alerts = [], issues = [], sensorReadings = {}, onExpandDetails, isLive, onGoLive, onMinimizeChange }) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const colorMode = useColorModeValue('light', 'dark');

  const handleToggle = () => { const next = !isMinimized; setIsMinimized(next); onMinimizeChange?.(next); };
  
  const activeSensors = Object.keys(sensorReadings).length;
  const alertSeverity = getAlertSeverity(alerts.length);
  const bgColor = getBgColor(alertSeverity, colorMode);
  
  const assetCountsByType = useMemo(() => assets.reduce((acc, asset) => { const type = asset.type || 'unknown'; acc[type] = (acc[type] || 0) + 1; return acc; }, {}), [assets]);
  const advancedStats = useMemo(() => {
    const criticalAlerts = alerts.filter(a => a.severity === 'critical').length;
    const highPriorityIssues = issues.filter(i => i.priority === 'high').length;
    const operationalAssets = assets.filter(a => a.status === 'operational').length;
    const healthPercentage = assets.length > 0 ? Math.round((operationalAssets / assets.length) * 100) : 0;
    return { criticalAlerts, highPriorityIssues, operationalAssets, healthPercentage, totalSensorReadings: Object.values(sensorReadings).filter(Boolean).length };
  }, [alerts, issues, assets, sensorReadings]);

  const handleExportData = () => {
    const exportData = { timestamp: new Date().toISOString(), summary: { totalAssets: assets.length, activeAlerts: alerts.length, openIssues: issues.length, activeSensors, systemHealth: advancedStats.healthPercentage }, assetsByType: assetCountsByType, alerts: alerts.map(a => ({ id: a.id, severity: a.severity, message: a.message })) };
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const linkElement = document.createElement('a'); linkElement.setAttribute('href', dataUri); linkElement.setAttribute('download', `system-overview-${Date.now()}.json`); linkElement.click();
  };

  const textColor = useColorModeValue('white', 'white');
  const iconColor = useColorModeValue('whiteAlpha.900', 'whiteAlpha.900');
  // --- UPDATE: hoisted out of the MenuList JSX below, where they were being
  // called mid-render as hooks inside a nested element's props.
  const menuBg = useColorModeValue('white', 'gray.800');
  const menuColor = useColorModeValue('gray.800', 'white');
  // --- END UPDATE

  return (
    <MotionBox position="absolute" bottom="0" left="0" right="0" bg={bgColor} color={textColor} borderTopRadius="xl" boxShadow="0 -8px 24px rgba(0,0,0,0.4)" zIndex={1000} initial={{ y: 100 }} animate={{ y: 0 }} transition={{ duration: 0.3 }}>
      <Flex align="center" p={4} borderBottomWidth={isMinimized ? '0' : '1px'} borderColor="whiteAlpha.300">
        <HStack spacing={3} flex={1}>
          <Icon as={FaInfoCircle} boxSize={5} color={iconColor} />
          <VStack align="start" spacing={0}>
            <Text fontWeight="800" fontSize="md">System Overview</Text>
            {!isMinimized && (
              <HStack spacing={2} fontSize="xs" opacity={0.8}>
                <HStack spacing={1}><Icon as={FaServer} boxSize={3} /><Text>{assets.length} Assets</Text></HStack><Text>•</Text>
                <HStack spacing={1}><Icon as={FaSignal} boxSize={3} /><Text>{activeSensors} Sensors</Text></HStack>
                {advancedStats.criticalAlerts > 0 && (<><Text>•</Text><Badge colorScheme="red" fontSize="2xs" px={2} borderRadius="full">{advancedStats.criticalAlerts} Critical</Badge></>)}
              </HStack>
            )}
          </VStack>
        </HStack>

        <HStack spacing={2}>
          {isLive ? (
            <MotionFlex align="center" gap={2} px={3} py={1} bg="green.500" borderRadius="full" animate={{ opacity: [1, 0.7, 1] }} transition={{ duration: 2, repeat: Infinity }}>
              <Box w="2" h="2" bg="white" borderRadius="full" /><Text fontSize="xs" fontWeight="700">LIVE</Text>
            </MotionFlex>
          ) : (
            <Button size="sm" colorScheme="green" onClick={onGoLive} leftIcon={<FaSignal />} fontWeight="700" boxShadow="md">Go Live</Button>
          )}
          <Tooltip label="Advanced Statistics" hasArrow><IconButton icon={<FaChartLine />} size="sm" variant={showAdvanced ? "solid" : "outline"} colorScheme="whiteAlpha" onClick={() => setShowAdvanced(!showAdvanced)} aria-label="Advanced stats" /></Tooltip>
          <Menu>
            <MenuButton as={IconButton} icon={<MdMoreVert />} size="sm" variant="ghost" color={textColor} _hover={{ bg: 'whiteAlpha.300' }} aria-label="More options" />
            <MenuList bg={menuBg} color={menuColor}>
              <MenuItem icon={<FaExpand />} onClick={onExpandDetails}>View Full Details</MenuItem>
              <MenuItem icon={<FaDownload />} onClick={handleExportData}>Export Data</MenuItem>
              <MenuItem icon={<FaCog />}>Settings</MenuItem>
            </MenuList>
          </Menu>
          <IconButton aria-label={isMinimized ? 'Show Overview' : 'Hide Overview'} icon={isMinimized ? <ChevronUpIcon /> : <ChevronDownIcon />} size="sm" onClick={handleToggle} variant="ghost" color={textColor} _hover={{ bg: 'whiteAlpha.300' }} />
        </HStack>
      </Flex>

      <AnimatePresence>
        {!isMinimized && (
          <MotionBox initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>
            <Box px={4} pt={3}>
              <HStack justify="space-between" mb={2}><Text fontSize="xs" fontWeight="700" opacity={0.9}>System Health</Text><Text fontSize="xs" fontWeight="700">{advancedStats.healthPercentage}%</Text></HStack>
              <Progress value={advancedStats.healthPercentage} size="sm" colorScheme={advancedStats.healthPercentage > 80 ? 'green' : advancedStats.healthPercentage > 50 ? 'yellow' : 'red'} borderRadius="full" bg="whiteAlpha.300" />
            </Box>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} p={4}>
              <Stat><StatLabel fontSize="xs" opacity={0.8}>Total Assets</StatLabel><StatNumber fontSize="2xl" fontWeight="800">{assets.length}</StatNumber><StatHelpText fontSize="2xs" opacity={0.7}><Icon as={FaCity} mr={1} />{advancedStats.operationalAssets} operational</StatHelpText></Stat>
              <Stat><StatLabel fontSize="xs" opacity={0.8}>Active Alerts</StatLabel><StatNumber fontSize="2xl" fontWeight="800" color="red.300">{alerts.length}</StatNumber><StatHelpText fontSize="2xs" opacity={0.7}><StatArrow type="increase" />{advancedStats.criticalAlerts} critical</StatHelpText></Stat>
              <Stat><StatLabel fontSize="xs" opacity={0.8}>Open Issues</StatLabel><StatNumber fontSize="2xl" fontWeight="800" color="yellow.300">{issues.length}</StatNumber><StatHelpText fontSize="2xs" opacity={0.7}><Icon as={FaExclamationTriangle} mr={1} />{advancedStats.highPriorityIssues} high priority</StatHelpText></Stat>
              <Stat><StatLabel fontSize="xs" opacity={0.8}>Live Sensors</StatLabel><StatNumber fontSize="2xl" fontWeight="800">{activeSensors}</StatNumber><StatHelpText fontSize="2xs" opacity={0.7}><Icon as={FaSignal} mr={1} />{advancedStats.totalSensorReadings} readings</StatHelpText></Stat>
            </SimpleGrid>
            <Collapse in={showAdvanced} animateOpacity>
              <Box px={4} pb={4}>
                <Divider borderColor="whiteAlpha.300" mb={4} />
                <Text fontSize="xs" fontWeight="700" mb={3} opacity={0.9}>Asset Distribution</Text>
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                  {Object.entries(assetCountsByType).map(([type, count]) => (
                    <Box key={type} p={3} bg="whiteAlpha.200" borderRadius="md" borderWidth="1px" borderColor="whiteAlpha.300">
                      <Text fontSize="2xs" fontWeight="600" opacity={0.8} textTransform="uppercase">{type}</Text>
                      <Text fontSize="xl" fontWeight="800" mt={1}>{count}</Text>
                      <Progress value={(count / assets.length) * 100} size="xs" colorScheme="whiteAlpha" mt={2} borderRadius="full" bg="whiteAlpha.300" />
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>
            </Collapse>
          </MotionBox>
        )}
      </AnimatePresence>
    </MotionBox>
  );
};

// ============================================================================
// SECTION 5: LIVE READINGS PAGE (Main Command Center)
// ============================================================================

function LiveReadingsPage() {
  const logger      = useRef(new DebugLogger('LiveReadingsPage')).current;
  const perfMonitor = useRef(new PerformanceMonitor()).current;

  // ── Core data state ──
  const [latestReadings,        setLatestReadings]       = useState({});
  const [pipelines,             setPipelines]            = useState([]);
  const [sensors,               setSensors]              = useState([]);
  const [assets,                setAssets]               = useState([]);
  const [sensorPipelineLinks, setSensorPipelineLinks] = useState({});

  // ── Map Layer & Fullscreen State ──
  const [isMapFullScreen, setIsMapFullScreen] = useState(false);
  const [isInspectingClearance, setIsInspectingClearance] = useState(false);
  const [visibleLayers, setVisibleLayers] = useState({
    pipelines: true,
    roads: true,
    pressureSensors: true,
    waterLevelSensors: true,
    assets: true,
    zones: true,
    anomalies: true,
  });

  const handleToggleLayer = useCallback((layerKey) => {
    setVisibleLayers(prev => ({ ...prev, [layerKey]: !prev[layerKey] }));
  }, []);

  const handleToggleFullScreen = useCallback(() => {
    setIsMapFullScreen(prev => !prev);
  }, []);

  // ── Water-level state ──
  const [waterLevelReadings, setWaterLevelReadings] = useState({});
  const [waterLevelHistory,  setWaterLevelHistory]  = useState({}); 

  // =========================================================================
  // ENTERPRISE FIX: USE GLOBAL CONNECTION
  // We no longer manage our own 'connecting' state locally. 
  // We pull it straight from the global NotificationContext.
  // =========================================================================
  const { isConnected } = useNotifications();
  const [isConnecting,       setIsConnecting]       = useState(false);
  const [authError,          setAuthError]          = useState(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [lastMessageTime,    setLastMessageTime]    = useState(null);

  // ── UI state ──
  const [isLoadingMapData,        setIsLoadingMapData]       = useState(true);
  const [mapDataError,            setMapDataError]           = useState(null);
  const [isRefreshing,            setIsRefreshing]           = useState(false);
  const [isDrawingPipeline,       setIsDrawingPipeline]      = useState(false);
  const [drawnPipelineCoords,     setDrawnPipelineCoords]    = useState(null);
  const [placementCoords,         setPlacementCoords]        = useState(null);
  const [isPlacingSensor,         setIsPlacingSensor]        = useState(false);
  const [isSubmittingPlacement, setIsSubmittingPlacement] = useState(false);
  const [selectedPipeline,        setSelectedPipeline]       = useState(null);
  const [selectedSensor,          setSelectedSensor]         = useState(null);
  const [selectedAsset,           setSelectedAsset]          = useState(null); 
  const [highlightedSensorId, setHighlightedSensorId] = useState(null);
  const [isLinkingMode, setIsLinkingMode] = useState(false);
  const [sensorToLink,  setSensorToLink]  = useState(null);

  // ── Unified Map & Table Selection State ──
  const [selectedEntity, setSelectedEntity] = useState({ type: null, id: null });

  // Unified selection handler passed to both Map and Tables
  const handleSelectEntity = useCallback((type, item) => {
    if (!item) {
      setSelectedEntity({ type: null, id: null });
      return;
    }
    const id = String(item.id || item.device_id || item.deviceId);
    setSelectedEntity({ type, id });

    if (type === 'pipeline') {
      setSelectedPipeline(item);
    } else if (type === 'sensor') {
      setSelectedSensor(item);
      setHighlightedSensorId(id);
    } else if (type === 'asset' || type === 'well') {
      setSelectedAsset(item);
    }
  }, []);

  const [isAssetLinkingMode, setIsAssetLinkingMode] = useState(false);
  const [sensorToLinkAsset, setSensorToLinkAsset] = useState(null);
  const { isOpen: isAssetLinkDrawerOpen, onOpen: onAssetLinkDrawerOpen, onClose: onAssetLinkDrawerClose } = useDisclosure();

  // --- UPDATE: pipeline clearance editing state --------------------------
  const [pipelineToEdit, setPipelineToEdit] = useState(null);
  const { isOpen: isPipelineEditOpen, onOpen: onPipelineEditOpen, onClose: onPipelineEditClose } = useDisclosure();
  // --- END UPDATE

  const [searchQuery,    setSearchQuery]    = useState('');
  const [filterStatus,   setFilterStatus]   = useState('all');
  const [showOnlyLinked, setShowOnlyLinked] = useState(false);
  const [debugLogs,      setDebugLogs]      = useState([]);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [debugFilter,    setDebugFilter]    = useState('all');
  const [statistics, setStatistics] = useState({ totalSensors: 0, activeSensors: 0, linkedSensors: 0, totalPipelines: 0, messagesReceived: 0, lastUpdateRate: 0 });

  const isMounted            = useRef(true);
  // --- UPDATE (sensor tables v2)
  const loadSeqRef           = useRef(0);      // newest loadMapData run; older runs never apply results
  const lastLoadAtRef        = useRef(0);      // when the last load finished (reconnect catch-up throttle)
  const hasLoadedRef         = useRef(false);  // the cache paints only the very first load
  const sensorsRef           = useRef([]);     // last good sensor list (an empty answer never blanks it)
  const isConnectedRef       = useRef(isConnected); // read by the mount effect without re-running it
  const wasConnectedRef      = useRef(isConnected);
  // --- END UPDATE
  const navigate             = useNavigate();
  const toast                = useToast();
  const messageCountRef      = useRef(0);
  const lastUpdateCheckRef   = useRef(Date.now());

  const { isOpen: isPipelineModalOpen,    onOpen: onPipelineModalOpen,    onClose: onPipelineModalClose }    = useDisclosure();
  const { isOpen: isPlaceSensorModalOpen, onOpen: onPlaceSensorModalOpen, onClose: onPlaceSensorModalClose } = useDisclosure();
  const { isOpen: isInfoDrawerOpen,       onOpen: onInfoDrawerOpen,       onClose: onInfoDrawerClose }       = useDisclosure();
  const { isOpen: isLinkDrawerOpen,       onOpen: onLinkDrawerOpen,       onClose: onLinkDrawerClose }       = useDisclosure();
  const { isOpen: isTwinDrawerOpen,       onOpen: onTwinDrawerOpen,       onClose: onTwinDrawerClose }       = useDisclosure();
  const { isOpen: isDebugDrawerOpen,      onOpen: onDebugDrawerOpen,      onClose: onDebugDrawerClose }      = useDisclosure();
  const { isOpen: isSensorDetailsOpen, onOpen: onSensorDetailsOpen, onClose: onSensorDetailsClose } = useDisclosure();
  const { isOpen: isHistoryModalOpen, onOpen: onHistoryModalOpen, onClose: onHistoryModalClose } = useDisclosure();

  const isMobile = useBreakpointValue({ base: true, md: false });
  const bgColor                 = useColorModeValue('gray.50',  'gray.900');
  const headingColor            = useColorModeValue('gray.800', 'white');
  const cardBg                  = useColorModeValue('white',    'gray.800');
  const cardBorderColor         = useColorModeValue('gray.200', 'gray.700');
  const textColor               = useColorModeValue('gray.700', 'gray.200');
  const mutedTextColor          = useColorModeValue('gray.600', 'gray.400');
  const headerGradient          = useColorModeValue('linear(to-r, blue.500, purple.600)', 'linear(to-r, blue.400, purple.500)');
  const mapCardGradient         = useColorModeValue('linear(to-br, white, blue.50)',       'linear(to-br, gray.800, blue.900)');
  
  const linkSensorBoxBg         = useColorModeValue('blue.50',  'blue.900');
  const linkSensorButtonHoverBg = useColorModeValue('blue.50',  'blue.900');
  const inputBg                 = useColorModeValue('white',    'gray.700');
  const debugRowBg              = useColorModeValue('gray.100', 'gray.800');
  const debugLogVStackBg        = useColorModeValue('gray.50',  'gray.900');
  // --- UPDATE: hoisted out of the TabList JSX below
  const tabListBg               = useColorModeValue('gray.100', 'gray.700');
  // --- END UPDATE

  // =========================================================================
  // ENTERPRISE FIX: Caching & Memoization
  // =========================================================================
  
 // Memoize grouped sensors
  // --- UPDATE (sensor tables v2): every sensor lands in exactly one table (was: 'pressure' / no type →
  // Pressure, exactly 'water_level' → Water Level, anything else → neither)
  const { pressure: pressureSensors, waterLevel: waterLevelSensors } = useMemo(() => splitSensorsByKind(sensors), [sensors]);
  // Tab badges = rows each table shows (its sensors ∪ devices sending its readings)
  const pressureRowCount   = useMemo(() => countTableRows(pressureSensors, latestReadings), [pressureSensors, latestReadings]);
  const waterLevelRowCount = useMemo(() => countTableRows(waterLevelSensors, waterLevelReadings), [waterLevelSensors, waterLevelReadings]);
  useEffect(() => { sensorsRef.current = sensors; }, [sensors]);
  // --- END UPDATE

  const systemAlerts = useMemo(() => {
    const alerts = [];
    Object.entries(latestReadings).forEach(([id, data]) => {
       if (data.psi > 100 || data.psi < 20) alerts.push({ id, severity: 'critical', message: `Pressure anomaly on ${id} (${data.psi} PSI)`});
    });
    return alerts;
  }, [latestReadings]);

  // ── Reactive AI/ML Flood Inferences State ──
  const [aiFloodInferences, setAiFloodInferences] = useState({});

  // --- UPDATE (wells v1): Well & Drainage Intelligence
  // AssetService.fetchAssets() is paged/cached, so wells could be missing from this page. The intel feed
  // returns EVERY well in the register; wells it adds are merged into `mergedAssets`, which now feeds the
  // map, the flood model, the AI weather engine and the Wells tab. Server flood predictions from the feed
  // seed the AI pipeline on load (before any `ai_flood_prediction` event arrives); live events still win.
  const wellIntel = useWellIntel();
  const [explainWellId, setExplainWellId] = useState(null);
  const [wellTrackerTab, setWellTrackerTab] = useState('risk');
  // --- UPDATE (wells v1.2): "Drainage Well" (and other well-family types) are shown as wells everywhere on
  // this page (map, flood model, Wells tab); the register's own type is kept in `asset_type`.
  const mergedAssets = useMemo(() => {
    const seen = new Set();
    const out = assets.map((a) => {
      const w = wellIntel.byId.get(String(a.id));
      const family = isWellType(a.type) && String(a.type).toLowerCase() !== 'well';
      const base = family ? { ...a, type: 'well', asset_type: a.type, well_kind: 'drainage' } : a;
      if (!w) return base;
      seen.add(String(a.id));
      return { ...base, intel: w.intel, well_kind: w.well_kind, register: w.register, inspection_status: w.inspection?.status, ai_predictions: a.ai_predictions || w.ai_predictions };
    });
    wellIntel.wells.forEach((w) => { if (!seen.has(String(w.id))) out.push(w); });
    return out;
  }, [assets, wellIntel.wells, wellIntel.byId]);
  const mergedServerPredictions = useMemo(
    () => ({ ...wellIntel.serverPredictions, ...aiFloodInferences }),
    [wellIntel.serverPredictions, aiFloodInferences]
  );
  const explainWell = explainWellId ? wellIntel.byId.get(String(explainWellId)) || null : null;
  const openWellExplainer = useCallback((wellOrId) => {
    const id = wellOrId && typeof wellOrId === 'object' ? (wellOrId.id ?? wellOrId.wellId) : wellOrId;
    if (id !== undefined && id !== null) setExplainWellId(String(id));
  }, []);
  const handleWellWorkOrder = useCallback((well, recommendation) => {
    if (!well) return;
    navigate('/admin/work-orders', { state: { woDraft: buildWellWorkOrderDraft(well, recommendation) } });
  }, [navigate]);
  const handleOpenWellWorkOrder = useCallback((wo) => {
    if (wo?.id) navigate(`/admin/work-orders?search=${encodeURIComponent(wo.id)}`);
  }, [navigate]);
  const handleOpenWellTracker = useCallback((tab) => {
    setWellTrackerTab(tab || 'risk');
    const el = typeof document !== 'undefined' ? document.getElementById('flowsense-well-ops') : null;
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);
  // --- END UPDATE

  // --- UPDATE: server ML stream telemetry (shown in the AI command center)
  const [aiServerStream, setAiServerStream] = useState({ lastAt: null, count: 0 });
  // --- END UPDATE

  useEffect(() => {
    const handleAiFloodEvent = (event) => {
      const data = event?.payload || event?.data || event;
      // --- UPDATE: accept batches ([...] or { predictions: [...] }) as well as single predictions
      const batch = Array.isArray(data) ? data : Array.isArray(data?.predictions) ? data.predictions : null;
      if (batch) {
        batch.forEach((item) => handleAiFloodEvent(item));
        return;
      }
      // --- END UPDATE
      if (data && (data.wellId || data.well_id)) {
        const wellId = String(data.wellId || data.well_id);
        setAiFloodInferences((prev) => ({
          ...prev,
          [wellId]: {
            // --- UPDATE: keep every extra field the ML service sends (confidence, factors, forecast,
            // health_score, rul, recommendations, model_version …); the original four keys below win.
            ...data,
            receivedAt: Date.now(),
            // --- END UPDATE
            predictedSeverity: data.severity || data.predicted_severity,
            floodRiskProbability: data.probability ?? data.flood_probability,
            estimatedMinutesToOvertopping: data.estMinutes ?? data.estimated_minutes_to_overtopping,
            weatherInfluence: data.weather || data.weather_influence,
          },
        }));
        // --- UPDATE
        setAiServerStream((s) => ({ lastAt: Date.now(), count: s.count + 1 }));
        // --- END UPDATE
      }
    };

    webSocketService.subscribe('ai_flood_prediction', handleAiFloodEvent);
    // --- UPDATE: batch event name used by bulk ML runs
    webSocketService.subscribe('ai_flood_prediction_batch', handleAiFloodEvent);
    // --- END UPDATE
    return () => {
      webSocketService.unsubscribe('ai_flood_prediction', handleAiFloodEvent);
      // --- UPDATE
      webSocketService.unsubscribe('ai_flood_prediction_batch', handleAiFloodEvent);
      // --- END UPDATE
    };
  }, []);

  // --- UPDATE: AI weather & ML intelligence (weather feed + edge model + server merge)
  const handleNewCriticalPredictions = useCallback((items) => {
    const first = items[0];
    const name = first.well.name || `Well ${first.well.id}`;
    const eta = first.prediction.estimatedMinutesToOvertopping;
    toast({
      title: items.length > 1 ? `🧠 ${items.length} wells at critical flood risk` : `🧠 AI: ${name} at critical flood risk`,
      description: `${Math.round((Number(first.prediction.floodRiskProbability) || 0) * 100)}% overflow probability${
        eta !== null && eta !== undefined ? `, overtopping ~${eta} min` : ''
      } (${first.prediction.source === 'edge' ? 'edge model' : 'server ML'}).`,
      status: 'warning',
      duration: 8000,
      isClosable: true,
      position: 'top-right',
    });
  }, [toast]);

  const aiIntel = useAiWeatherIntelligence({
    // --- UPDATE (wells v1): all wells + server predictions from the well intel feed
    assets: mergedAssets,
    sensors,
    waterLevelReadings,
    waterLevelHistory,
    serverPredictions: mergedServerPredictions,
    // --- END UPDATE
    onNewCritical: handleNewCriticalPredictions,
  });
  // --- END UPDATE

  // =========================================================================
  // 🌊 AI FLOOD INUNDATION MODEL (Memoized, Signature-Cached, Weather/AI-Fed)
  // =========================================================================
  const floodAnalytics = useFloodRiskModel(
    mergedAssets, // --- UPDATE (wells v1)
    sensors,
    waterLevelReadings,
    useMemo(() => ({ hazardRadiusMeters: 400, pipeHazardRadiusMeters: 250 }), []),
    mergedServerPredictions // --- UPDATE (wells v1)
  );

  // Toast notification triggered on newly detected inundation hazard
  const prevAlertCount = useRef(0);
  useEffect(() => {
    if (floodAnalytics.roadAlerts.length > prevAlertCount.current) {
      const top = floodAnalytics.roadAlerts[0];
      const roadsSummary = (top.impactedRoads || []).map((r) => r.roadName).join(', ');
      toast({
        title: '⚠️ Road Flood Hazard Detected',
        description: `High water level at ${top.wellName}. Adjacent transportation corridors at risk: ${roadsSummary}`,
        status: 'error',
        duration: 9000,
        isClosable: true,
        position: 'top-right',
      });
    }
    prevAlertCount.current = floodAnalytics.roadAlerts.length;
  }, [floodAnalytics.roadAlerts, toast]);

  // --- UPDATE: pipeline clearance integrity ------------------------------
  // pipelineService.fetchPipelines now returns depth_m, status, geometry SRID
  // and an is_clearance_visible flag on every record. This summarises the
  // register so the two numbers that decide whether excavation clearance is
  // trustworthy are visible on the page that manages pipelines.
  const pipelineIntegrity = useMemo(() => {
    const total = pipelines.length;
    if (total === 0) return { total: 0, sridBroken: 0, missingDepth: 0, depthPct: 100, noGeometry: 0 };

    let sridBroken = 0, missingDepth = 0, noGeometry = 0;
    pipelines.forEach(p => {
      if (p.geometry_srid_ok === false) sridBroken += 1;
      if (p.depth_m === null || p.depth_m === undefined) missingDepth += 1;
      if (!Array.isArray(p.path_coordinates) || p.path_coordinates.length < 2) noGeometry += 1;
    });

    return {
      total,
      sridBroken,
      missingDepth,
      noGeometry,
      depthPct: Number(((100 * (total - missingDepth)) / total).toFixed(1)),
    };
  }, [pipelines]);
  // --- END UPDATE

  // ============================================================================
  // BULLETPROOF WEBSOCKET HANDLER
  // Extracts payload regardless of whether it's wrapped in { type, payload } or raw data
  // ============================================================================
  const handleWebSocketMessage = useCallback((message) => {
    if (!isMounted.current || !message) return;
    try {
      const data = message.payload || message.data || message;
      const deviceId = data.device_id || data.deviceId || data.device;
      if (!deviceId) return;

      if (data.psi !== undefined || data.pressure !== undefined) {
        const pressure = data.psi !== undefined ? data.psi : data.pressure;
        const timestamp = data.timestamp || data.received_at || new Date().toISOString();
        
        setLatestReadings(prev => ({ 
          ...prev, 
          [deviceId]: { psi: pressure, timestamp, status: data.status || 'normal', quality: data.quality || 100 } 
        }));
        
        messageCountRef.current += 1;
        setLastMessageTime(Date.now());
      } 
      else if (data.level !== undefined) {
        const timestamp = data.timestamp || data.received_at || new Date().toISOString();
        setWaterLevelReadings(prev => ({ ...prev, [deviceId]: { level: data.level, timestamp } }));
        setWaterLevelHistory(prev => { 
          const existing = prev[deviceId] || []; 
          return { ...prev, [deviceId]: [{ level: data.level, timestamp }, ...existing].slice(0, 50) }; 
        });
        
        messageCountRef.current += 1;
        setLastMessageTime(Date.now());
      }

    } catch (error) { 
      logger.error('Error processing WebSocket message', { error: error.message }); 
    }
  }, [logger]);

  const handleWebSocketStatus = useCallback((statusEvent) => {
    if (!isMounted.current) return;
    const { status, reason } = statusEvent;
    switch (status) {
      case 'connecting':
      case 'reconnecting':
        setIsConnecting(true); 
        setAuthError(null);
        break;
      case 'connected':
        setIsConnecting(false); 
        setAuthError(null); 
        setReconnectAttempts(0);
        break;
      case 'disconnected':
        setIsConnecting(false);
        break;
      case 'error':
      case 'failed':
        setIsConnecting(false); 
        setReconnectAttempts(prev => prev + 1);
        if (reason === 'Invalid token received' || reason === 'Authentication failed') {
          setAuthError('Session expired. Please log in again.'); clearAuthData();
          setTimeout(() => { if (isMounted.current) navigate('/auth/sign-in'); }, 1500);
        }
        break;
      default: break;
    }
  }, [navigate]);

  // =========================================================================
  // ENTERPRISE FIX: Caching Map Data in SessionStorage for Instant Re-renders
  // =========================================================================
  const loadMapData = useCallback(async (retryCount = 0) => {
    const MAX_RETRIES = 3;
    perfMonitor.startTimer('loadMapData');
    const seq = ++loadSeqRef.current; // --- UPDATE (sensor tables v2): only the newest run applies results
    const isCurrent = () => isMounted.current && seq === loadSeqRef.current;
    
    const token = getAuthToken();
    if (!token) { clearAuthData(); setMapDataError('Authentication required.'); setIsLoadingMapData(false); return; }
    
    // 1. Optimistically load from cache immediately so map paints instantly
    // --- UPDATE (sensor tables v2): only on the very first load. Re-applying the cache on every refresh
    // swapped the live list for an older copy and back again.
    if (retryCount === 0 && !hasLoadedRef.current) {
      const cachedAssets = cacheRead('fs_cache_assets');
      const cachedPipelines = cacheRead('fs_cache_pipelines');
      const cachedSensors = cacheRead('fs_cache_sensors');
      if (cachedAssets) setAssets(cachedAssets);
      if (cachedPipelines) setPipelines(cachedPipelines);
      if (cachedSensors && cachedSensors.length) setSensors(cachedSensors);
      setIsLoadingMapData(!(cachedAssets || cachedPipelines || (cachedSensors && cachedSensors.length)));
    }
    // --- END UPDATE

    setMapDataError(null);
    
    // 2. Fetch fresh data in the background
    try {
      const results = await Promise.allSettled([
          PipelineService.fetchPipelines(), 
          SensorService.fetchSensors(), 
          fetchSensorPipelineLinks(),
          AssetService.fetchAssets()
      ]);
      
      if (!isCurrent()) return; // --- UPDATE (sensor tables v2)
      const [pipelinesResult, sensorsResult, linksResult, assetsResult] = results;
      let errors = [];
      let loadedSensors = [];
      
      if (pipelinesResult.status === 'fulfilled') { 
          const validPipes = (pipelinesResult.value || []).filter(p => DataValidator.validatePipeline(p).isValid);
          setPipelines(validPipes); 
          cacheWrite('fs_cache_pipelines', validPipes); // --- UPDATE (sensor tables v2): can't throw

          // --- UPDATE: surface register integrity in the debug log.
          // fetchPipelines attaches a non-enumerable _integrity summary; it is
          // dropped by JSON.stringify into sessionStorage, so read it here.
          const integrity = pipelinesResult.value?._integrity;
          if (integrity && (integrity.unparseableGeometry > 0 || integrity.missingDepth > 0)) {
            logger.warn('Pipeline register integrity', integrity);
          }
          // --- END UPDATE
      } else { errors.push(`Pipelines: ${pipelinesResult.reason?.message || 'request failed'}`); }
      
 // --- UPDATE (sensor tables v2): an empty answer while sensors are on screen is treated as a bad
      // answer (fetchSensors returns [] for any unexpected shape), not as "every sensor was deleted";
      // the last good list stays and the problem is shown.
      if (sensorsResult.status === 'fulfilled') {
        const valid = (Array.isArray(sensorsResult.value) ? sensorsResult.value : []).filter(s => DataValidator.validateSensor(s).isValid);
        if (valid.length === 0 && sensorsRef.current.length > 0) {
          loadedSensors = sensorsRef.current;
          logger.warn('Sensor list came back empty — keeping the last good list', { kept: sensorsRef.current.length });
          errors.push('Sensors: the server returned no sensors — showing the last loaded list');
        } else {
          loadedSensors = valid;
          setSensors(valid); 
          cacheWrite('fs_cache_sensors', valid);
        }
      } else { loadedSensors = sensorsRef.current; errors.push(`Sensors: ${sensorsResult.reason?.message || 'request failed'}`); }
      // --- END UPDATE
      
      if (linksResult.status === 'fulfilled') { setSensorPipelineLinks(linksResult.value || {}); } 
      if (assetsResult.status === 'fulfilled') { 
          setAssets(assetsResult.value || []); 
          cacheWrite('fs_cache_assets', assetsResult.value || []); // --- UPDATE (sensor tables v2): can't throw
      } 

      // 3. Fetch initial water level & pressure readings
      const wSensors = loadedSensors.filter(isWaterLevelSensor); // --- UPDATE (sensor tables v2): same rule as the tables
      if (wSensors.length > 0) {
          wSensors.forEach(async (s) => {
              try {
                  const res = await fetch(`${API_BASE_URL}/api/v1/water-level/${s.device_id}?limit=1`, { headers: { Authorization: `Bearer ${token}` } });
                  if(res.ok) {
                      const json = await res.json();
                      if(json.data && json.data.length > 0) {
                          const latest = json.data[0];
                          if (isCurrent()) setWaterLevelReadings(prev => ({ ...prev, [s.device_id]: { level: latest.level, timestamp: latest.timestamp } })); // --- UPDATE
                      }
                  }
              } catch(err) { }
          });
      }

      const pSensors = loadedSensors.filter(isPressureSensor); // --- UPDATE (sensor tables v2): same rule as the tables
      if (pSensors.length > 0) {
          try {
              const res = await fetch(`${API_BASE_URL}/api/v1/readings?limit=200`, { headers: { Authorization: `Bearer ${token}` } });
              if (res.ok) {
                  const json = await res.json();
                  if (json.data && Array.isArray(json.data)) {
                      const initialReadings = {};
                      json.data.reverse().forEach(r => {
                          const dId = r.device_id || r.deviceId;
                          if (dId) initialReadings[dId] = { psi: r.psi, timestamp: r.timestamp || r.received_at, status: r.status || 'normal' };
                      });
                      if (isCurrent()) setLatestReadings(prev => ({ ...prev, ...initialReadings })); // --- UPDATE
                  }
              }
          } catch(err) {}
      }

      if (errors.length > 0) { setMapDataError(`Failed to load some map data:\n${errors.join('\n')}`); } else setMapDataError(null);
      hasLoadedRef.current = true; // --- UPDATE (sensor tables v2)
    } catch (error) {
      if (!isCurrent()) return; // --- UPDATE (sensor tables v2)
      if (retryCount < MAX_RETRIES) { setTimeout(() => { if (isMounted.current && seq === loadSeqRef.current) loadMapData(retryCount + 1); }, 2000 * (retryCount + 1)); return; }
      // --- UPDATE (sensor tables v2): keep what is on screen — a failed refresh used to empty every table
      // and the map (setPipelines([]) / setSensors([]) / setAssets([]))
      setMapDataError(`${error.message || 'An unknown error occurred while loading map data.'} — showing the last loaded data.`);
      // --- END UPDATE
    } finally {
      // --- UPDATE (sensor tables v2)
      if (isCurrent()) { setIsLoadingMapData(false); lastLoadAtRef.current = Date.now(); }
      perfMonitor.endTimer('loadMapData');
      // --- END UPDATE
    }
  }, [perfMonitor, logger]);

  const handleFetchHistory = async (deviceId) => {
      const token = getAuthToken();
      try {
          const response = await fetch(`${API_BASE_URL}/api/v1/water-level/${deviceId}?limit=50`, { headers: { Authorization: `Bearer ${token}` } });
          if(response.ok) {
              const json = await response.json();
              if (json.data) { setWaterLevelHistory(prev => ({ ...prev, [deviceId]: json.data })); }
          }
      } catch (err) { toast({ title: "History Error", description: "Could not load sensor history.", status: "error" }); }
  };

  const fetchSensorPipelineLinks = async () => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error('No auth token');
      const response = await fetch(`${API_BASE_URL}/api/v1/sensors/pipeline-links`, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to fetch links: ${response.statusText}`);
      const data = await response.json();
      const rawLinks = data.data || data.links || data || [];
      const formattedLinks = {};
      if (Array.isArray(rawLinks)) { rawLinks.forEach(link => { if (link.sensor_id && link.pipeline_id) { formattedLinks[String(link.sensor_id).trim()] = link.pipeline_id; } }); } 
      else if (typeof rawLinks === 'object') { Object.entries(rawLinks).forEach(([key, val]) => { formattedLinks[String(key).trim()] = val; }); }
      return formattedLinks;
    } catch (error) { return {}; }
  };

  const linkSensorToPipeline = async (sensorId, pipelineId) => {
    const token = getAuthToken(); if (!token) throw new Error('No auth token');
    const response = await fetch(`${API_BASE_URL}/api/v1/sensors/${sensorId}/link-pipeline`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ pipeline_id: pipelineId }) });
    if (!response.ok) { const e = await response.json(); throw new Error(e.message || `Failed to link sensor: ${response.statusText}`); }
    const data = await response.json();
    setSensorPipelineLinks(prev => ({ ...prev, [sensorId]: pipelineId }));
    toast({ title: 'Sensor Linked', description: `Sensor ${String(sensorId)} linked to pipeline successfully`, status: 'success', duration: 3000, isClosable: true });
    return data;
  };

  const unlinkSensorFromPipeline = async (sensorId) => {
    const token = getAuthToken(); if (!token) throw new Error('No auth token');
    const response = await fetch(`${API_BASE_URL}/api/v1/sensors/${sensorId}/unlink-pipeline`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
    if (!response.ok) { const e = await response.json(); throw new Error(e.message || `Failed to unlink sensor: ${response.statusText}`); }
    setSensorPipelineLinks(prev => { const u = { ...prev }; delete u[sensorId]; return u; });
    toast({ title: 'Sensor Unlinked', description: `Sensor ${String(sensorId)} unlinked from pipeline`, status: 'info', duration: 3000, isClosable: true });
  };

  const handleSavePlacement = async (submissionData) => {
    setIsSubmittingPlacement(true);
    const token = getAuthToken();
    if (!token) { toast({ title: 'Auth Error', description: 'Authentication required', status: 'error' }); setIsSubmittingPlacement(false); return; }
    try {
      if (!submissionData.deviceId || !submissionData.coordinates) throw new Error('Missing sensor ID or coordinates for placement.');
      const cv = DataValidator.validateCoordinates(submissionData.coordinates);
      if (!cv.isValid) throw new Error(`Invalid coordinates: ${cv.errors.join(', ')}`);
      
      const updatePayload = { latitude: submissionData.coordinates.lat, longitude: submissionData.coordinates.lng, linked_entity_type: submissionData.linkType || 'none', linked_entity_id: submissionData.selectedEntityId || null, placed_at: new Date().toISOString(), placed_by: 'current_user' };
      await SensorService.updateSensorDetails(submissionData.deviceId, updatePayload);
      toast({ title: 'Sensor Placed', description: `Location updated for sensor ${String(submissionData.deviceId)}`, status: 'success', duration: 3000, isClosable: true });
      
      if (submissionData.pipelineId && submissionData.linkType !== 'pipeline') {
        try { await linkSensorToPipeline(submissionData.deviceId, submissionData.pipelineId); }
        catch (linkError) { toast({ title: 'Placement Successful, Linking Failed', description: 'Sensor placed but could not be linked to pipeline', status: 'warning', duration: 5000, isClosable: true }); }
      }
      onPlaceSensorModalClose(); await loadMapData(); setPlacementCoords(null); setIsPlacingSensor(false);
    } catch (error) { toast({ title: 'Placement Failed', description: String(error.message || 'Could not place sensor'), status: 'error', duration: 7000, isClosable: true });
    } finally { setIsSubmittingPlacement(false); }
  };

  const handleAddPipeline = () => {
    if (isDrawingPipeline) { handleCancelDrawing(); return; }
    setIsDrawingPipeline(true); setIsPlacingSensor(false); setIsLinkingMode(false);
    toast({ title: 'Draw Pipeline Mode', description: 'Click points on the map. Double-click to finish. Right-click to undo a point.', status: 'info', duration: 8000, isClosable: true });
  };
  
  const handleCancelDrawing = () => { setIsDrawingPipeline(false); setDrawnPipelineCoords(null); toast({ title: 'Drawing Cancelled', status: 'warning', duration: 2000, isClosable: true }); };

  const handlePlaceSensor = () => {
    if (isDrawingPipeline) handleCancelDrawing();
    setIsPlacingSensor(prev => !prev);
    if (!isPlacingSensor) { setIsLinkingMode(false); toast({ title: 'Place Sensor Mode', description: 'Click map to select sensor location.', status: 'info', duration: 3000, isClosable: true }); }
    else { setPlacementCoords(null); toast({ title: 'Place Sensor Mode Off', status: 'warning', duration: 2000, isClosable: true }); }
  };

  const handleMapClickForPlacement = useCallback((coords) => {
    if (!isPlacingSensor) return;
    const validation = DataValidator.validateCoordinates(coords);
    if (!validation.isValid) { toast({ title: 'Invalid Location', description: 'Please select a valid location on the map', status: 'error', duration: 3000, isClosable: true }); return; }
    setPlacementCoords(coords); onPlaceSensorModalOpen();
  }, [isPlacingSensor, onPlaceSensorModalOpen, toast]);

  const handlePipelineComplete = useCallback((coordinates) => {
    setIsDrawingPipeline(false);
    if (Array.isArray(coordinates) && coordinates.length >= 2) {
      const invalid = coordinates.filter(c => !DataValidator.validateCoordinates({ lat: c.lat, lng: c.lng }).isValid);
      if (invalid.length > 0) { toast({ title: 'Invalid Pipeline', description: 'Some coordinates are invalid', status: 'error', duration: 3000, isClosable: true }); return; }
      setDrawnPipelineCoords(coordinates); onPipelineModalOpen();
    } else { toast({ title: 'Drawing Incomplete', description: 'Pipeline requires at least two points.', status: 'warning', duration: 3000, isClosable: true }); setDrawnPipelineCoords(null); }
  }, [onPipelineModalOpen, toast]);

  const handlePipelineCreateSuccess = () => { onPipelineModalClose(); loadMapData(); setDrawnPipelineCoords(null); };

const handlePipelineSelect = useCallback((pipeline) => {
    handleSelectEntity('pipeline', pipeline);
    onInfoDrawerOpen();
  }, [handleSelectEntity, onInfoDrawerOpen]);
  // --- UPDATE: pipeline clearance editing handlers ------------------------
  const handleEditPipeline = useCallback((pipeline) => {
    setPipelineToEdit(pipeline);
    onPipelineEditOpen();
  }, [onPipelineEditOpen]);

  const handlePipelineSaved = useCallback((updated) => {
    // Patch the row in place so the coverage tiles and worklist update
    // immediately, then reload in the background for server truth.
    if (updated?.id) {
      setPipelines(prev => prev.map(p => (p.id === updated.id ? { ...p, ...updated } : p)));
    }
    setPipelineToEdit(null);
    loadMapData();
  }, [loadMapData]);

  const handleClearanceRefresh = useCallback(() => { loadMapData(); }, [loadMapData]);
  // --- END UPDATE

  const handleAssetSelect = useCallback((asset) => {
    const isWell = (asset?.type || '').toLowerCase() === 'well';
    handleSelectEntity(isWell ? 'well' : 'asset', asset);
    setSelectedSensor(null);
    setSelectedPipeline(null);
  }, [handleSelectEntity]);

  const handleSensorSelect = useCallback((sensor) => {
    const sensorId = sensor?.device_id || sensor?.id || sensor?.deviceId;
    handleSelectEntity('sensor', sensor);
    setSelectedPipeline(null);
    setSelectedAsset(null);
    onSensorDetailsOpen();

    let linkStatusText = 'Not linked';
    if (sensor?.linked_entity_type === 'pipeline') { 
      linkStatusText = `Linked to Pipeline ${sensor.linked_entity_id}`; 
    } else if (sensor?.linked_entity_type && sensor?.linked_entity_type !== 'none') { 
      linkStatusText = `Linked to Asset ${sensor.linked_entity_id}`; 
    } else if (sensorPipelineLinks[sensorId]) { 
      linkStatusText = `Linked to Pipeline ${sensorPipelineLinks[sensorId]}`; 
    }

    const reading = latestReadings[sensorId];
    toast({ 
      title: `Sensor ${String(sensorId)}`, 
      description: `${reading ? `${reading.psi} PSI` : 'No reading'} | ${linkStatusText}`, 
      status: 'info', 
      duration: 4000, 
      isClosable: true, 
      position: 'top' 
    });
  }, [handleSelectEntity, sensorPipelineLinks, latestReadings, toast, onSensorDetailsOpen]);


  const handleViewHistory = useCallback((sensorId) => {
    const sensor = sensors.find(s => s.device_id === sensorId || s.id === sensorId || s.deviceId === sensorId) || { deviceId: sensorId };
    setSelectedSensor(sensor);
    onHistoryModalOpen();
  }, [sensors, onHistoryModalOpen]);

  const handleStartLinking = (sensorOrId) => {
    const sensorId = typeof sensorOrId === 'string' ? sensorOrId : (sensorOrId?.device_id || sensorOrId?.id || sensorOrId?.deviceId);
    if (!sensorId) { toast({ title: 'Linking Error', description: 'Cannot link an invalid sensor.', status: 'error', duration: 3000, isClosable: true }); return; }
    setSensorToLink(sensorId); setIsLinkingMode(true); setIsPlacingSensor(false); setIsDrawingPipeline(false);
    toast({ title: 'Link Sensor to Pipeline', description: `Click on a pipeline to link sensor ${String(sensorId)}`, status: 'info', duration: 5000, isClosable: true });
    onLinkDrawerOpen();
  };

  const handlePipelineClickForLinking = useCallback(async (pipeline) => {
    if (!isLinkingMode || !sensorToLink) return;
    const sensorId = sensorToLink; 
    try {
      await linkSensorToPipeline(sensorId, pipeline.id);
      setIsLinkingMode(false); setSensorToLink(null); onLinkDrawerClose(); await loadMapData();
    } catch (error) { 
      const safeErrorMsg = typeof error.message === 'object' ? JSON.stringify(error.message) : String(error.message || error);
      toast({ title: 'Linking Failed', description: safeErrorMsg, status: 'error', duration: 5000, isClosable: true }); 
    }
  }, [isLinkingMode, sensorToLink, onLinkDrawerClose, toast, loadMapData]);

  const handleCancelLinking = () => { setIsLinkingMode(false); setSensorToLink(null); onLinkDrawerClose(); toast({ title: 'Linking Cancelled', status: 'warning', duration: 2000, isClosable: true }); };

  // --- ASSET LINKING HANDLERS ---
  const handleStartAssetLinking = (sensorOrId) => {
    const sensorId = typeof sensorOrId === 'string' ? sensorOrId : (sensorOrId?.device_id || sensorOrId?.id || sensorOrId?.deviceId);
    if (!sensorId) { toast({ title: 'Linking Error', status: 'error' }); return; }
    setSensorToLinkAsset(sensorId);
    setIsAssetLinkingMode(true); setIsLinkingMode(false); setIsPlacingSensor(false); setIsDrawingPipeline(false);
    onAssetLinkDrawerOpen();
  };

  const handleAssetClickForLinking = async (asset) => {
    if (!sensorToLinkAsset) return;
    try {
        await SensorService.assignSensorToAsset(sensorToLinkAsset, asset.id);
        toast({ title: 'Bound to Asset', description: `Sensor linked to ${asset.name || asset.type}`, status: 'success', duration: 3000 });
        onAssetLinkDrawerClose();
        setSensorToLinkAsset(null);
        setIsAssetLinkingMode(false);
        await loadMapData();
    } catch (error) {
        toast({ title: 'Binding Failed', description: error.message, status: 'error', duration: 5000 });
    }
  };

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try { await loadMapData(); toast({ title: 'Data Refreshed', status: 'success', duration: 2000, isClosable: true, position: 'bottom-right' }); }
    catch (error) {} finally { setIsRefreshing(false); }
  }, [loadMapData, toast]);

  const handleUpdateWellMetadata = useCallback(async (wellId, inspectionData) => {
    const token = getAuthToken();
    if (!token) throw new Error('Authentication required.');

    const res = await fetch(`${API_BASE_URL}/api/v1/assets/${wellId}/well-inspection`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(inspectionData),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || `Server returned HTTP ${res.status}`);
    }

    await loadMapData();
    wellIntel.refresh(); // --- UPDATE (wells v1): the server also pushes well_intel_update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMapData, wellIntel.refresh]);

  const filteredSensors = useMemo(() => {
    let filtered = [...sensors];
    if (searchQuery) { const q = searchQuery.toLowerCase(); filtered = filtered.filter(s => (s.device_id || s.id || '').toString().toLowerCase().includes(q) || (s.name || '').toLowerCase().includes(q)); }
    if (filterStatus !== 'all') {
      filtered = filtered.filter(s => {
        const id = s.device_id || s.id; const reading = latestReadings[id];
        if (filterStatus === 'active')   return reading !== undefined;
        if (filterStatus === 'inactive') return reading === undefined;
        if (filterStatus === 'alert')    return reading && (reading.psi > 100 || reading.psi < 20);
        return true;
      });
    }
    if (showOnlyLinked) filtered = filtered.filter(s => sensorPipelineLinks[s.device_id || s.id] !== undefined || (s.linked_entity_type && s.linked_entity_type !== 'none'));
    return filtered;
  }, [sensors, searchQuery, filterStatus, showOnlyLinked, latestReadings, sensorPipelineLinks]);

  // =========================================================================
  // ENTERPRISE FIX: Memoize displayed assets to prevent heavy re-renders
  // =========================================================================
  const displayedAssets = useMemo(() => {
    return mergedAssets.filter(asset => { // --- UPDATE (wells v1): includes every registered well
      // Always show wells/tanks/etc
      if (asset.type && asset.type.toLowerCase() === 'well') return true;
      if (asset.type && asset.type.toLowerCase() === 'tank') return true;
      // Also show anything that has a sensor explicitly linked to it
      const isLinked = sensors.some(s => String(s.linked_entity_id) === String(asset.id));
      return isLinked;
    });
  }, [mergedAssets, sensors]);

  // ── Layer-Filtered Collections for GisMapView ──
  const filteredMapPipelines = useMemo(() => 
    visibleLayers.pipelines ? pipelines : [], 
    [visibleLayers.pipelines, pipelines]
  );

  const filteredMapSensors = useMemo(() => {
    return filteredSensors.filter(s => {
      const isWaterLevel = isWaterLevelSensor(s); // --- UPDATE (sensor tables v2): same rule as the tables
      if (isWaterLevel) return visibleLayers.waterLevelSensors;
      return visibleLayers.pressureSensors;
    });
  }, [filteredSensors, visibleLayers.pressureSensors, visibleLayers.waterLevelSensors]);

  const filteredMapAssets = useMemo(() => {
    return displayedAssets.filter(a => {
      const isRoad = a.type === 'road' || a.type === 'street';
      if (isRoad) return visibleLayers.roads;
      return visibleLayers.assets;
    });
  }, [displayedAssets, visibleLayers.roads, visibleLayers.assets]);

  useEffect(() => {
    isMounted.current = true;
    // --- UPDATE: named status handlers so cleanup removes the exact functions that were
    // registered (the inline arrows previously couldn't be removed, so listeners accumulated
    // on every reconnect because this effect re-runs when isConnected changes).
    const onWsConnect      = () => handleWebSocketStatus({ status: 'connected' });
    const onWsDisconnect   = (reason) => handleWebSocketStatus({ status: 'disconnected', reason });
    const onWsReconnecting = () => handleWebSocketStatus({ status: 'reconnecting' });
    // --- END UPDATE
    const initialize = async () => {
      try {
        await loadMapData();
        if (isMounted.current) {
          webSocketService.subscribe('new_pressure_reading',    handleWebSocketMessage);
          webSocketService.subscribe('new_water_level_reading', handleWebSocketMessage);
          webSocketService.subscribe('psi_update',              handleWebSocketMessage);
          webSocketService.subscribe('telemetry',               handleWebSocketMessage);
          
          // --- UPDATE: named handlers (see above)
          webSocketService.on('connect',      onWsConnect);
          webSocketService.on('disconnect',   onWsDisconnect);
          webSocketService.on('reconnecting', onWsReconnecting);
          // --- END UPDATE
          
          // --- UPDATE (sensor tables v2): read through a ref so this effect does not re-run on every flip
          if (isConnectedRef.current) {
              handleWebSocketStatus({ status: 'connected' });
          }
          // --- END UPDATE
        }
      } catch (error) {}
    };
    initialize();
    
    return () => {
      isMounted.current = false;
      webSocketService.unsubscribe('new_pressure_reading',    handleWebSocketMessage);
      webSocketService.unsubscribe('new_water_level_reading', handleWebSocketMessage);
      webSocketService.unsubscribe('psi_update',              handleWebSocketMessage);
      webSocketService.unsubscribe('telemetry',               handleWebSocketMessage);
      
      // --- UPDATE: remove the same functions that were registered
      webSocketService.off('connect',      onWsConnect);
      webSocketService.off('disconnect',   onWsDisconnect);
      webSocketService.off('reconnecting', onWsReconnecting);
      // --- END UPDATE
    };
    // --- UPDATE (sensor tables v2): isConnected removed — every live-connection flip used to re-run the
    // whole load (cache → fresh list), which is the "refresh" after which sensors disappeared
  }, [loadMapData, handleWebSocketMessage, handleWebSocketStatus]);

  // --- UPDATE (sensor tables v2): after a real reconnect, catch up once (readings missed while offline),
  // at most every 30 s; the first connect right after opening the page does not reload again.
  useEffect(() => {
    isConnectedRef.current = isConnected;
    const was = wasConnectedRef.current;
    wasConnectedRef.current = isConnected;
    if (!isConnected || was) return;
    handleWebSocketStatus({ status: 'connected' });
    if (lastLoadAtRef.current && Date.now() - lastLoadAtRef.current > 30000) loadMapData();
  }, [isConnected, handleWebSocketStatus, loadMapData]);
  // --- END UPDATE

  useEffect(() => {
    const unsubscribe = logger.subscribe(logEntry => setDebugLogs(prev => [...prev.slice(-99), logEntry]));
    return unsubscribe;
  }, [logger]);

  // --- UPDATE: live KPI strip
  const liveKpis = useMemo(() => {
    const now = Date.now();
    const fresh = (ts) => ts && now - new Date(ts).getTime() < 300000;
    const pressureOnline = isConnected
      ? Object.values(latestReadings).filter((r) => fresh(r?.timestamp)).length
      : 0;
    const wlReporting = Object.keys(waterLevelReadings).length;
    const { critical = 0, high = 0 } = aiIntel.insights.counts || {};
    const rain24 = aiIntel.weather?.next24hRainMm;
    return [
      { key: 'pressure', label: 'Pressure online', value: `${pressureOnline}/${pressureSensors.length}`, icon: MdSensors, color: pressureOnline ? 'green.500' : 'gray.400' },
      { key: 'wl', label: 'Water level reporting', value: `${wlReporting}/${waterLevelSensors.length}`, icon: MdWaterDrop, color: 'cyan.500' },
      { key: 'ai', label: 'AI flood risk', value: critical + high, sub: critical ? `${critical} critical` : high ? `${high} high` : 'all stable', icon: MdAutoAwesome, color: critical ? 'red.500' : high ? 'orange.400' : 'purple.500' },
      { key: 'roads', label: 'Road hazards', value: floodAnalytics.roadAlerts.length, sub: floodAnalytics.summary?.impactedRoadCount ? `${floodAnalytics.summary.impactedRoadCount} roads` : null, icon: MdWarning, color: floodAnalytics.roadAlerts.length ? 'red.500' : 'gray.400' },
      { key: 'rain', label: 'Rain next 24h', value: rain24 !== undefined && rain24 !== null ? `${rain24} mm` : '—', icon: MdGrain, color: rain24 >= 25 ? 'orange.400' : 'blue.500' },
      { key: 'alerts', label: 'Pressure anomalies', value: systemAlerts.length, icon: MdError, color: systemAlerts.length ? 'red.500' : 'gray.400' },
    ];
  }, [isConnected, latestReadings, waterLevelReadings, pressureSensors.length, waterLevelSensors.length, aiIntel.insights, aiIntel.weather, floodAnalytics.roadAlerts, floodAnalytics.summary, systemAlerts]);

  const handleAiSelectWell = useCallback((well) => {
    handleSelectEntity('well', well);
  }, [handleSelectEntity]);

  const aiCriticalWellCount = aiIntel.insights.counts?.critical || 0;
  // --- END UPDATE

  return (
    <Box pt={{ base: '10px', md: '10px', xl: '10px' }} bg={bgColor} minH="100vh" position="relative" pb="150px">

      {/* ═══ HEADER ═══ */}
      <MotionFlex initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} justify="space-between" align="center" mb={6} wrap="wrap" gap={4} px={{ base: 2, md: 0 }}>
        <Box>
          <Flex align="center" gap={3} mb={2}>
            <MotionBox initial={{ scale: 0 }} animate={{ scale: 1, rotate: 360 }} transition={{ duration: 0.6, type: 'spring' }} bgGradient={headerGradient} p={3} borderRadius="xl" boxShadow="lg">
              <Icon as={MdWaves} color="white" boxSize={7} />
            </MotionBox>
            <Box>
              <Heading color={headingColor} size={isMobile ? 'lg' : 'xl'} fontWeight="800" bgGradient={headerGradient} bgClip="text">Live Readings & Network Map</Heading>
              <Text fontSize="sm" color={mutedTextColor} fontWeight="500">Real-time sensor monitoring and pipeline management</Text>
            </Box>
          </Flex>
        </Box>
        <HStack spacing={3}>
          <AnimatedConnectionStatus isConnected={isConnected} isConnecting={isConnecting} authError={authError} />
          <Tooltip label="Refresh Data" hasArrow><MotionBox whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}><IconButton icon={<Icon as={MdRefresh} />} size="md" onClick={handleRefresh} isLoading={isRefreshing} variant="ghost" colorScheme="blue" borderRadius="full" /></MotionBox></Tooltip>
          <Tooltip label="Debug Panel" hasArrow><MotionBox whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}><IconButton icon={<Icon as={MdBugReport} />} size="md" onClick={onDebugDrawerOpen} variant="ghost" colorScheme={showDebugPanel ? 'purple' : 'gray'} borderRadius="full" /></MotionBox></Tooltip>
        </HStack>
      </MotionFlex>

      {/* --- UPDATE: live KPI strip */}
      <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} spacing={3} mb={5} px={{ base: 2, md: 0 }}>
        {liveKpis.map((k, i) => (
          <MotionBox
            key={k.key}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 * i }}
            bg={cardBg}
            borderWidth="1px"
            borderColor={cardBorderColor}
            borderRadius="xl"
            p={3}
            boxShadow="sm"
            position="relative"
            overflow="hidden"
          >
            <Box position="absolute" left={0} top={0} bottom={0} w="3px" bg={k.color} />
            <Flex justify="space-between" align="start">
              <Box>
                <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color={mutedTextColor}>{k.label}</Text>
                <Text fontSize="xl" fontWeight="800" color={k.color} lineHeight="1.2">{k.value}</Text>
                {k.sub && <Text fontSize="10px" color={mutedTextColor}>{k.sub}</Text>}
              </Box>
              <Icon as={k.icon} boxSize={5} color={k.color} opacity={0.8} />
            </Flex>
          </MotionBox>
        ))}
      </SimpleGrid>
      {/* --- END UPDATE */}

      {/* --- UPDATE: PIPELINE CLEARANCE INTEGRITY BANNER --------------------
          The SRID failure is invisible everywhere else: those mains raise a
          database error inside the subsurface scan, which reaches the crew as
          a blocked work order with no usable explanation. */}
      <AnimatePresence>
        {pipelineIntegrity.sridBroken > 0 && (
          <MotionBox key="pipeline-srid" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }} mb={4}>
            <Alert status="error" borderRadius="xl" variant="subtle" shadow="sm" alignItems="flex-start">
              <AlertIcon as={MdErrorOutline} />
              <Box flex={1}>
                <AlertTitle fontSize="sm">
                  {pipelineIntegrity.sridBroken} pipeline(s) cannot be used by excavation clearance scans
                </AlertTitle>
                <AlertDescription fontSize="xs" display="block" mt={1}>
                  Their geometry is not SRID 4326, so the subsurface scan raises a database error and
                  returns INDETERMINATE. Run the SRID repair migration — recording depth will not fix it.
                </AlertDescription>
              </Box>
            </Alert>
          </MotionBox>
        )}
      </AnimatePresence>
      {/* --- END UPDATE */}

      {/* ═══ ACTIONS BAR ═══ */}
      <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
        <Flex direction={{ base: 'column', md: 'row' }} gap={4} mb={6} bg={cardBg} p={4} borderRadius="2xl" borderWidth="1px" borderColor={cardBorderColor} boxShadow="md">
          <HStack spacing={3} flex={1} wrap="wrap">
            <MotionBox whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              {!isDrawingPipeline
                ? <Button size="md" leftIcon={<Icon as={MdTimeline} />} colorScheme="purple" onClick={handleAddPipeline} isDisabled={isLinkingMode} borderRadius="xl" fontWeight="600" boxShadow="sm">Draw Pipeline</Button>
                : <Button size="md" leftIcon={<Icon as={MdEditOff}  />} variant="outline" colorScheme="red" onClick={handleCancelDrawing} borderRadius="xl" fontWeight="600">Cancel Drawing</Button>
              }
            </MotionBox>
            <MotionBox whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button size="md" leftIcon={<Icon as={MdMyLocation} />} colorScheme={isPlacingSensor ? 'red' : 'orange'} onClick={handlePlaceSensor} isDisabled={isDrawingPipeline || isLinkingMode} borderRadius="xl" fontWeight="600" boxShadow="sm">
                {isPlacingSensor ? 'Cancel Placement' : 'Place Sensor'}
              </Button>
            </MotionBox>
          </HStack>
          <HStack spacing={3} flexWrap="wrap">
            <InputGroup size="md" maxW="220px">
              <InputLeftElement><Icon as={MdSearch} color="gray.400" /></InputLeftElement>
              <Input placeholder="Search sensors..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} borderRadius="xl" bg={inputBg} />
            </InputGroup>
            <Select size="md" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} maxW="160px" borderRadius="xl" bg={inputBg}>
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="alert">Alerts</option>
            </Select>
            <FormControl display="flex" alignItems="center" w="auto">
              <FormLabel htmlFor="linked-filter" mb="0" fontSize="sm" mr={2} whiteSpace="nowrap">Linked Only</FormLabel>
              <Switch id="linked-filter" size="md" isChecked={showOnlyLinked} onChange={e => setShowOnlyLinked(e.target.checked)} colorScheme="blue" />
            </FormControl>
          </HStack>
        </Flex>
      </MotionBox>

      {/* --- UPDATE: AI weather & flood intelligence */}
      <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
        <AiWeatherCommandCenter
          weather={aiIntel.weather}
          weatherStatus={aiIntel.weatherStatus}
          insights={aiIntel.insights}
          isConnected={isConnected}
          serverStream={aiServerStream}
          onReloadWeather={aiIntel.reloadWeather}
          onSelectWell={handleAiSelectWell}
        />
        {/* --- UPDATE (weather v2): rain right now — models + staff reports (no rain gauge on the network) */}
        <RainNowPanel weather={aiIntel.weather} weatherStatus={aiIntel.weatherStatus} onReport={aiIntel.reportRain} />
        {/* --- END UPDATE */}
      </MotionBox>
      {/* --- END UPDATE */}

      {/* --- UPDATE (wells v1): Well & Drainage Intelligence strip (live) */}
      <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.28 }}>
        <WellIntelCommandStrip
          summary={wellIntel.summary}
          isLoading={wellIntel.isLoading}
          error={wellIntel.error}
          generatedAt={wellIntel.generatedAt}
          liveEvents={wellIntel.liveEvents}
          isConnected={isConnected}
          onSelectWell={openWellExplainer}
          onOpenTracker={handleOpenWellTracker}
          onRefresh={wellIntel.refresh}
        />
      </MotionBox>
      {/* --- END UPDATE */}

      {/* ═══ MAP ═══ */}
      <MotionBox 
        initial={{ opacity: 0, scale: 0.95 }} 
        animate={{ opacity: 1, scale: 1 }} 
        transition={{ delay: 0.3, duration: 0.4 }}
        position={isMapFullScreen ? 'fixed' : 'relative'}
        top={isMapFullScreen ? 0 : 'auto'}
        left={isMapFullScreen ? 0 : 'auto'}
        right={isMapFullScreen ? 0 : 'auto'}
        bottom={isMapFullScreen ? 0 : 'auto'}
        w={isMapFullScreen ? '100vw' : '100%'}
        h={isMapFullScreen ? '100vh' : 'auto'}
        zIndex={isMapFullScreen ? 9999 : 1}
      >
        <Box 
          bg={cardBg} 
          bgGradient={mapCardGradient} 
          p={isMapFullScreen ? 0 : 6} 
          borderRadius={isMapFullScreen ? 'none' : '3xl'} 
          boxShadow="2xl" 
          borderWidth={isMapFullScreen ? '0px' : '2px'} 
          borderColor={cardBorderColor} 
          mb={isMapFullScreen ? 0 : 6} 
          h={isMapFullScreen ? '100vh' : { base: '380px', md: '480px', lg: '600px' }} 
          position="relative" 
          overflow="hidden"
        >
          {!isMapFullScreen && (
            <Box position="absolute" top="0" left="0" right="0" h="4px" bgGradient={headerGradient} borderTopRadius="3xl" />
          )}

          {/* 🚨 FLOOD INUNDATION ALERT BAR (Interactive Dispatch & Corridors HUD) */}
          <FloodInundationAlert
            roadAlerts={floodAnalytics.roadAlerts}
            highRiskPipes={floodAnalytics.highRiskPipes}
            summary={floodAnalytics.summary}
            onFocusLocation={(lat, lng, meta) => {
              handleSelectEntity(meta?.type === 'road' ? 'asset' : 'well', { 
                latitude: lat, 
                longitude: lng,
                id: meta?.roadId || meta?.wellId 
              });
            }}
            // --- UPDATE (wells v1): Dispatch opens a pre-filled flood-response work order (it only showed a
            // toast before — no crew was ever dispatched); (i) opens the AI explainer for the well.
            onDispatchCrew={(alert) => {
              const well = wellIntel.byId.get(String(alert.wellId));
              if (well) {
                handleWellWorkOrder(well, (well.intel?.recommendations || []).find((r) => r.code === 'DISPATCH_CREW')
                  || { code: 'DISPATCH_CREW', priority: 'urgent', text: `Secure road corridors around ${alert.wellName}: ${(alert.impactedRoads || []).map((r) => r.roadName).join(', ')}.` });
              } else {
                navigate('/admin/work-orders', { state: { woDraft: { title: `Flood response — ${alert.wellName}`, priority: 'critical', asset_id: alert.wellId, well_id: alert.wellId, inspection_source: 'flowsense_flood_alert', description: `Adjacent roads at risk: ${(alert.impactedRoads || []).map((r) => r.roadName).join(', ')}` } } });
              }
            }}
            onViewDetails={(alert) => openWellExplainer(alert.wellId)}
            // --- END UPDATE
          />

          {/* Layer and Fullscreen Controls */}
          <MapFilterBar
            visibleLayers={visibleLayers}
            onToggleLayer={handleToggleLayer}
            isFullScreen={isMapFullScreen}
            onToggleFullScreen={handleToggleFullScreen}
            isInspectingClearance={isInspectingClearance}
            onToggleClearanceInspector={() => setIsInspectingClearance(prev => !prev)}
          />

          <GisMapView
            pipelines={filteredMapPipelines}
            sensors={filteredMapSensors}
            assets={filteredMapAssets}
            criticalWellIds={floodAnalytics.criticalWellIds} // 👈 Supplies the Set of overflowing wells
            selectedEntity={selectedEntity}
            sensorPipelineLinks={sensorPipelineLinks}
            readings={{ ...latestReadings, ...waterLevelReadings }}
            highlightedSensorId={highlightedSensorId}
            onPipelineClick={isLinkingMode ? handlePipelineClickForLinking : handlePipelineSelect}
            onSensorClick={handleSensorSelect}
            onSensorSelect={handleSensorSelect}
            onAssetSelect={handleAssetSelect}
            onMapClickForPlacement={isPlacingSensor ? handleMapClickForPlacement : undefined}
            isDrawingPipeline={isDrawingPipeline}
            isPlacingSensor={isPlacingSensor}
            isLinkingMode={isLinkingMode}
            onPipelineComplete={handlePipelineComplete}
            isLoadingData={isLoadingMapData}
            dataError={mapDataError}
            onRefreshPipelines={handleRefresh}
            isInspectingClearance={isInspectingClearance}
            onClearanceInspectionComplete={(res) => {
              if (res?.severity === 'CRITICAL') {
                toast({
                  title: 'Clearance Violation',
                  description: 'Active utility collision detected at target location.',
                  status: 'error',
                  duration: 5000,
                  isClosable: true
                });
              }
            }}
          />
        </Box>
      </MotionBox>

      {/* ═══ DATA TABLES ═══ */}
      <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
        <Box bg={cardBg} p={6} borderRadius="3xl" boxShadow="2xl" borderWidth="2px" borderColor={cardBorderColor} overflowX="auto">

          <Tabs variant="soft-rounded" colorScheme="blue" isLazy>
            <Flex justify="space-between" align="center" mb={5} flexWrap="wrap" gap={3}>
              <Box>
                <Heading size="lg" color={headingColor} fontWeight="700" mb={1}>Sensor Readings</Heading>
                <Text fontSize="sm" color={mutedTextColor}>Real-time data across all sensor types</Text>
              </Box>
              <TabList bg={tabListBg} p={1} borderRadius="xl">
                <Tab fontSize="sm" fontWeight="600" borderRadius="lg" _selected={{ bg: cardBg, boxShadow: 'sm', color: 'blue.500' }}>
                  <HStack spacing={2}>
                    <Icon as={MdSensors} />
                    <Text>Pressure</Text>
                    <Badge colorScheme="blue" borderRadius="full" fontSize="10px">{pressureRowCount}</Badge>{/* --- UPDATE (sensor tables v2): sensors, not readings */}
                  </HStack>
                </Tab>
                <Tab fontSize="sm" fontWeight="600" borderRadius="lg" _selected={{ bg: cardBg, boxShadow: 'sm', color: 'cyan.500' }}>
                  <HStack spacing={2}>
                    <Icon as={MdWaterDrop} />
                    <Text>Water Level</Text>
                    <Badge colorScheme="cyan" borderRadius="full" fontSize="10px">{waterLevelRowCount}</Badge>{/* --- UPDATE (sensor tables v2) */}
                  </HStack>
                </Tab>
                {/* 🚀 WELL MANAGEMENT TAB */}
                <Tab fontSize="sm" fontWeight="600" borderRadius="lg" _selected={{ bg: cardBg, boxShadow: 'sm', color: 'teal.500' }}>
                  <HStack spacing={2}>
                    <Icon as={MdWaterDrop} />
                    <Text>Well Management</Text>
                    <Badge colorScheme="teal" borderRadius="full" fontSize="10px">
                      {assets.filter(a => (a.type || '').toLowerCase() === 'well').length}
                    </Badge>
                    {/* --- UPDATE: AI critical count */}
                    {aiCriticalWellCount > 0 && (
                      <Tooltip label={`${aiCriticalWellCount} well(s) at critical AI flood risk`} hasArrow>
                        <Badge colorScheme="red" variant="solid" borderRadius="full" fontSize="10px" display="flex" alignItems="center" gap="2px">
                          <Icon as={MdAutoAwesome} boxSize={2.5} />{aiCriticalWellCount}
                        </Badge>
                      </Tooltip>
                    )}
                    {/* --- END UPDATE */}
                  </HStack>
                </Tab>
                {/* --- UPDATE: pipeline clearance tab --- */}
                <Tab fontSize="sm" fontWeight="600" borderRadius="lg" _selected={{ bg: cardBg, boxShadow: 'sm', color: 'purple.500' }}>
                  <HStack spacing={2}>
                    <Icon as={MdShield} />
                    <Text>Clearance</Text>
                    <Badge
                      colorScheme={pipelineIntegrity.sridBroken > 0 ? 'red' : pipelineIntegrity.missingDepth > 0 ? 'orange' : 'green'}
                      borderRadius="full" fontSize="10px"
                    >
                      {pipelineIntegrity.sridBroken > 0
                        ? pipelineIntegrity.sridBroken
                        : pipelineIntegrity.missingDepth}
                    </Badge>
                  </HStack>
                </Tab>
                {/* --- END UPDATE */}

                {/* --- UPDATE: pipeline directory tab --- */}
                <Tab fontSize="sm" fontWeight="600" borderRadius="lg" _selected={{ bg: cardBg, boxShadow: 'sm', color: 'purple.500' }}>
                  <HStack spacing={2}>
                    <Icon as={MdRouter} />
                    <Text>Pipelines</Text>
                    <Badge colorScheme="purple" borderRadius="full" fontSize="10px">{pipelines.length}</Badge>
                  </HStack>
                </Tab>
                {/* --- END UPDATE */}
              </TabList>
            </Flex>

            <TabPanels>
              <TabPanel px={0} pb={0}>
                <ReadingTable
                  sensors={pressureSensors}
                  readings={latestReadings}
                  sensorPipelineLinks={sensorPipelineLinks}
                  selectedSensorId={selectedEntity?.type === 'sensor' ? selectedEntity.id : null}
                  onSensorSelect={handleSensorSelect}
                  onLinkSensor={handleStartLinking}
                  onUnlinkSensor={unlinkSensorFromPipeline}
                  onViewHistory={handleViewHistory}
                />
              </TabPanel>

              <TabPanel px={0} pb={0}>
                <WaterLevelTable
                  sensors={waterLevelSensors}
                  readings={waterLevelReadings}
                  links={sensorPipelineLinks}
                  sensorHistory={waterLevelHistory}
                  selectedSensorId={selectedEntity?.type === 'sensor' ? selectedEntity.id : null}
                  isLoading={isLoadingMapData}
                  onLink={handleStartLinking}
                  onLinkAsset={handleStartAssetLinking} 
                  onUnlink={async (deviceId) => {
                    try { await unlinkSensorFromPipeline(deviceId); }
                    catch (err) { toast({ title: 'Unlink Failed', description: String(err.message || err), status: 'error', duration: 5000, isClosable: true }); }
                  }}
                  onSensorSelect={handleSensorSelect}
                  onHistory={handleViewHistory}
                  // --- UPDATE: merged server + edge predictions (AI FLOOD badge, AI sort, row forecast)
                  aiPredictions={aiIntel.predictions}
                  // --- END UPDATE
                />
              </TabPanel>

              {/* 🚀 WELL MANAGEMENT PANEL */}
              <TabPanel px={0} pb={0}>
                <WellManagementPanel
                  // --- UPDATE (wells v1): every well in the register (was: paged asset list only)
                  wells={mergedAssets.filter(a => (a.type || '').toLowerCase() === 'well')}
                  sensors={sensors}
                  readings={waterLevelReadings}
                  selectedWellId={selectedEntity?.type === 'well' ? selectedEntity.id : null}
                  // --- UPDATE: merged predictions (server ML wins while fresh; edge model fills gaps)
                  // and the page's shared weather feed (the panel skips its own fetch when given one)
                  aiPredictions={aiIntel.predictions}
                  weather={aiIntel.weather}
                  // --- END UPDATE
                  onSelectWell={(well) => handleSelectEntity('well', well)}
                  isLoading={isLoadingMapData}
                  onRefresh={handleRefresh}
                  onUpdateWellMetadata={handleUpdateWellMetadata}
                  // --- UPDATE (wells v1.3): drainage wells / catch basins / inspections from Well Intelligence
                  intelById={wellIntel.byId}
                  onOpenExplainer={openWellExplainer}
                  onCreateWorkOrder={handleWellWorkOrder}
                  // --- END UPDATE
                />
              </TabPanel>

              {/* --- UPDATE: pipeline clearance panel --- */}
              <TabPanel px={0} pb={0}>
                <PipelineClearancePanel
                  pipelines={pipelines}
                  isLoading={isLoadingMapData}
                  onRefresh={handleClearanceRefresh}
                  onEditPipeline={handleEditPipeline}
                />
              </TabPanel>
              {/* --- END UPDATE */}

              {/* --- UPDATE: pipeline directory panel --- */}
              <TabPanel px={0} pb={0}>
                <PipelineDirectory
                  pipelines={pipelines}
                  selectedPipelineId={selectedEntity?.type === 'pipeline' ? selectedEntity.id : null}
                  isLoading={isLoadingMapData}
                  onRefresh={handleClearanceRefresh}
                  onSelectPipeline={handlePipelineSelect}
                  onEditPipeline={handleEditPipeline}
                />
              </TabPanel>
              {/* --- END UPDATE */}
            </TabPanels>
          </Tabs>

        </Box>
      </MotionBox>

      {/* --- UPDATE (wells v1): well operations tracker + AI explainer */}
      <WellOpsTracker
        wells={wellIntel.wells}
        isLoading={wellIntel.isLoading}
        activeTab={wellTrackerTab}
        onTabChange={setWellTrackerTab}
        onSelectWell={openWellExplainer}
        onCreateWorkOrder={handleWellWorkOrder}
        onOpenWorkOrder={handleOpenWellWorkOrder}
      />
      <WellAiExplainer
        isOpen={!!explainWell}
        well={explainWell}
        onClose={() => setExplainWellId(null)}
        onLocate={(w) => { setExplainWellId(null); handleSelectEntity('well', w); }}
        onCreateWorkOrder={handleWellWorkOrder}
        onOpenWorkOrder={handleOpenWellWorkOrder}
        onViewHistory={(deviceId) => { setExplainWellId(null); handleViewHistory(deviceId); }}
      />
      {/* --- END UPDATE */}
      {/* --- UPDATE (wells v1.3): ground elevation backfill (TessaDEM) — coverage + run/cancel, live progress.
          Finishing a run refreshes Well Intelligence (well_intel_update) so terrain drivers appear. */}
      <Box mb={6}><ElevationBackfillPanel /></Box>
      {/* --- END UPDATE */}

      {/* ═══ MODALS & DRAWERS ═══ */}
      <CreatePipelineForm isOpen={isPipelineModalOpen} onClose={() => { setDrawnPipelineCoords(null); onPipelineModalClose(); }} onSuccess={handlePipelineCreateSuccess} initialCoordinates={drawnPipelineCoords} />
      
      <PlaceSensorModal 
        isOpen={isPlaceSensorModalOpen} 
        onClose={onPlaceSensorModalClose} 
        onSuccess={handleSavePlacement} 
        coordinates={placementCoords} 
        isLoading={isSubmittingPlacement} 
        pipelines={pipelines} 
        assets={assets} 
        enablePipelineLinking 
        availableSensors={sensors} 
      />

      {selectedPipeline && (
        <PipelineInfoDrawer
          pipeline={selectedPipeline}
          sensors={sensors}
          readings={{ ...latestReadings, ...waterLevelReadings }}
          /* --- UPDATE: link map resolves the attached-sensors list; onEdit
             surfaces the Edit button that opens PipelineEditDrawer. Both are
             optional props, so this is purely additive. */
          sensorPipelineLinks={sensorPipelineLinks}
          onEdit={(p) => { onInfoDrawerClose(); setSelectedPipeline(null); handleEditPipeline(p); }}
          /* --- END UPDATE */
          isOpen={isInfoDrawerOpen}
          onClose={() => { onInfoDrawerClose(); setSelectedPipeline(null); }}
        />
      )}

      {/* --- UPDATE: pipeline clearance edit drawer --- */}
      <PipelineEditDrawer
        isOpen={isPipelineEditOpen}
        onClose={() => { onPipelineEditClose(); setPipelineToEdit(null); }}
        pipeline={pipelineToEdit}
        onSaved={handlePipelineSaved}
      />
      {/* --- END UPDATE */}

      {/* --- DEBUG DRAWER --- */}
      <Drawer isOpen={isDebugDrawerOpen} placement="right" onClose={onDebugDrawerClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px"><HStack><Icon as={MdBugReport} color="purple.500" /><Text>Debug Panel</Text></HStack></DrawerHeader>
          <DrawerBody>
            <VStack spacing={4} align="stretch">
              <Box>
                <Heading size="sm" mb={3}>System Statistics</Heading>
                <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                  {[
                    ['Total Sensors',    statistics.totalSensors],
                    ['Active Sensors',   statistics.activeSensors],
                    ['Linked Sensors',   statistics.linkedSensors],
                    ['Total Pipelines',  statistics.totalPipelines],
                    ['Messages',         statistics.messagesReceived],
                    ['Update Rate',      `${statistics.lastUpdateRate} msg/s`],
                    ['Reconnects',       reconnectAttempts],
                    ['WL Sensors',       waterLevelSensors.length],        
                    ['WL Reporting',     Object.keys(waterLevelReadings).length], 
                    // --- UPDATE: clearance register stats
                    ['Pipelines Loaded',  pipelineIntegrity.total],
                    ['Depth Coverage',    `${pipelineIntegrity.depthPct}%`],
                    ['Bad Geometry SRID', pipelineIntegrity.sridBroken],
                    // --- END UPDATE
                  ].map(([k, v]) => (
                    <React.Fragment key={k}>
                      <Text fontSize="sm" fontWeight="600">{k}:</Text>
                      <Text fontSize="sm">{String(v)}</Text>
                    </React.Fragment>
                  ))}
                </Grid>
              </Box>
              <Divider />
              <Box>
                <Flex justify="space-between" align="center" mb={3}>
                  <Heading size="sm">Debug Logs</Heading>
                  <HStack>
                    <Select size="sm" value={debugFilter} onChange={e => setDebugFilter(e.target.value)} maxW="120px" borderRadius="md">
                      {['all','debug','info','warn','error','success'].map(v => <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>)}
                    </Select>
                    <Button size="sm" onClick={() => logger.clear()} colorScheme="red" variant="ghost">Clear</Button>
                  </HStack>
                </Flex>
                <VStack spacing={2} align="stretch" maxH="400px" overflowY="auto" bg={debugLogVStackBg} p={3} borderRadius="lg" borderWidth="1px" borderColor={cardBorderColor}>
                  {debugLogs.filter(l => debugFilter === 'all' ? true : l.level === debugFilter).map((log, index) => (
                    <Box key={index} p={3} bg={debugRowBg} borderRadius="md" fontSize="xs" fontFamily="mono" borderWidth="1px" borderColor={cardBorderColor}>
                      <Flex justify="space-between" mb={2}>
                        <Badge colorScheme={log.level === 'error' ? 'red' : log.level === 'warn' ? 'orange' : log.level === 'success' ? 'green' : log.level === 'info' ? 'blue' : 'gray'}>{log.level.toUpperCase()}</Badge>
                        <Text color="gray.500" fontSize="10px">{new Date(log.timestamp).toLocaleTimeString()}</Text>
                      </Flex>
                      <Text fontWeight="600" mb={1}>{typeof log.message === 'object' ? JSON.stringify(log.message) : String(log.message)}</Text>
                      {log.data && <Text color="gray.500" fontSize="10px" fontFamily="mono">{JSON.stringify(log.data, null, 2)}</Text>}
                    </Box>
                  ))}
                </VStack>
              </Box>
            </VStack>
          </DrawerBody>
          <DrawerFooter borderTopWidth="1px"><Button size="sm" onClick={onDebugDrawerClose} colorScheme="blue">Close</Button></DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* --- DIGITAL TWIN DRAWER --- */}
      <Drawer isOpen={isTwinDrawerOpen} placement="right" onClose={() => { onTwinDrawerClose(); setSelectedAsset(null); }} size="xl">
        <DrawerOverlay backdropFilter="blur(4px)" />
        <DrawerContent bg="transparent" boxShadow="none">
          <DigitalTwinOverlay 
            selectedAsset={selectedAsset} 
            sensors={sensors} 
            readings={{ ...latestReadings, ...waterLevelReadings }} 
            healthHistory={[]} 
            wsUrl={null}
            onCriticalAlarm={(alarms) => {
                toast({
                    title: "CRITICAL FAILURE IMMINENT",
                    description: `${alarms.length} critical alarms detected on ${selectedAsset?.name}`,
                    status: "error",
                    duration: 10000,
                    isClosable: true
                });
            }}
          />
        </DrawerContent>
      </Drawer>

      <Drawer isOpen={isLinkDrawerOpen} placement="bottom" onClose={handleCancelLinking} size="sm">
        <DrawerOverlay />
        <DrawerContent borderTopRadius="2xl">
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px"><HStack><Icon as={MdLink} color="blue.500" /><Text>Link Sensor to Pipeline</Text></HStack></DrawerHeader>
          <DrawerBody py={6}>
            {sensorToLink && (
              <VStack spacing={4} align="stretch">
                <Box p={4} bg={linkSensorBoxBg} borderRadius="xl" borderWidth="2px" borderColor="blue.500">
                  <Text fontWeight="bold" fontSize="lg" mb={1}>Sensor: {String(sensorToLink)}</Text>
                  <Text fontSize="sm" color={mutedTextColor}>Click on a pipeline on the map to link this sensor</Text>
                </Box>
                <Divider />
                <Heading size="sm">Available Pipelines</Heading>
                <VStack spacing={2} align="stretch">
                  {pipelines.map(pipeline => (
                    <Button key={pipeline.id} size="md" variant="outline" onClick={() => handlePipelineClickForLinking(pipeline)} w="full" justifyContent="flex-start" borderRadius="xl" borderWidth="2px" _hover={{ borderColor: 'blue.500', bg: linkSensorButtonHoverBg }}>
                        <HStack w="full" justify="space-between">
                          <HStack><Icon as={MdRouter} /><Text fontWeight="600">Pipeline {pipeline.id}</Text></HStack>
                          <Text fontSize="sm" color={mutedTextColor}>{pipeline.name || 'Unnamed'}</Text>
                        </HStack>
                      </Button>
                  ))}
                </VStack>
              </VStack>
            )}
          </DrawerBody>
          <DrawerFooter borderTopWidth="1px">
            <Button size="md" variant="outline" onClick={handleCancelLinking} colorScheme="red" borderRadius="xl">Cancel</Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* --- ASSET BINDING DRAWER --- */}
      <BindAssetDrawer 
        isOpen={isAssetLinkDrawerOpen}
        onClose={() => {
          onAssetLinkDrawerClose(); 
          setSensorToLinkAsset(null); 
          setIsAssetLinkingMode(false);
        }}
        sensorId={sensorToLinkAsset}
        assets={assets}
        onBind={handleAssetClickForLinking}
      />

      {/* --- DATA OVERLAY (BOTTOM PANEL) --- */}
      <DataOverlay 
        assets={assets} 
        alerts={systemAlerts} 
        issues={[]} 
        sensorReadings={{ ...latestReadings, ...waterLevelReadings }} 
        isLive={isConnected} 
        onGoLive={() => webSocketService.connect()} 
      />

      <ViewSensorDetails 
        isOpen={isSensorDetailsOpen} 
        onClose={onSensorDetailsClose} 
        sensor={selectedSensor} 
        liveReading={
          selectedSensor 
            ? (latestReadings[selectedSensor.deviceId || selectedSensor.device_id] || 
               waterLevelReadings[selectedSensor.deviceId || selectedSensor.device_id]) 
            : null
        }
        aiPredictions={aiIntel.predictions}
        onSelectEntity={handleSelectEntity}
      />

      <ViewHistory 
        isOpen={isHistoryModalOpen} 
        onClose={onHistoryModalClose} 
        sensor={selectedSensor} 
      />

    </Box>
  );
}

export default LiveReadingsPage;